import { GraphQLResponseV6 } from "@aws-amplify/api-graphql";
import {
  ADLFieldSectionMapping,
  AppointmentRecord,
  AppointmentRecordPatientCase,
  AppointmentStatus,
  AppointmentType,
  CreateAppointmentRecordInput,
  DataFieldValueMapping,
  Doctor,
  EmploymentType,
  FlexibleDateInput,
  InquiringParty,
  Location,
  MedicalAssistant,
  NotesType,
  OnsiteOpsConfig,
  OpsGlobalSettings,
  ParticipantType,
  Patient,
  PaymentStatus,
  ReportStatus,
  ReschedulingReason,
  SearchAppointmentsQueryVariables,
  UpdateAppointmentRecordInput,
  User,
} from "~/api/API";
import { createPatient } from "~/api/graphql/mutations";
import {
  notesByAppointmentrecordID,
  searchAppointmentsCustom,
} from "~/api/graphql/queries";
import {
  createAppointmentRecordPatientCaseReturningPairID,
  createAppointmentRecordReturningID,
  createNotesReturningID,
  createPatientCaseReturningID,
  deleteAppointmentRecordPatientCaseReturningID,
  deleteNotesReturningID,
  deletePatientCaseReturningID,
  updateAppointmentRecordReturningID,
  updateNotesReturningID,
  updatePatientCaseReturningID,
  updatePatientReturningID,
} from "~/api/graphql-custom/mutations";
import {
  getAppointmentForAuditLogs,
  getAppointmentOpinionRecap,
  getAppointmentRecordForDoctorOptimized,
  getAppointmentRecordOptimized,
  searchAppointmentsIdOnly,
} from "~/api/graphql-custom/queries";
import { LOCATION_INDEPENDENT_ONSITE_OP_TYPES } from "~/common/appointmentConstants";
import {
  convertFormDataServiceDetailToQueryInput,
  extractYearMonthFromDateISO,
  formatDateStringToISO,
  toLowerCase,
  toSentenceCase,
} from "~/common/converters";
import { DateISO } from "~/common/DateISO";
import { toDayIso, toStoredPair } from "~/common/PartialDate";
import { sortPatientCases } from "~/common/sortings";
import {
  daysBetweenISODates,
  generateAppointmentS3FileKey,
  generateDoctorYearMonths,
  getReevaluationTypeByFileKey,
  getTodayDateISOStr,
  normalizeNullableId,
  removeTypenames,
} from "~/common/utils";
import {
  DEFAULT_APPOINTMENT_GLOBAL_PARTITION_KEY,
  DEFAULT_DATE_ISO,
  DEFAULT_TIME_ISO,
} from "~/constants";
import {
  errorReporter,
  InternalError,
  toError,
  UnauthorizedAccessError,
  UserError,
} from "~/error-handling";
import {
  type GraphQLErrorLike,
  GraphQLErrorType,
  hasAnyErrorOfType,
} from "~/error-handling/graphqlErrors";
import {
  CombinedLambdaServices,
  WrappedServiceModule,
} from "~/hooks/useServices";
import { GraphQLClientWrapper, graphqlClient } from "~/lib/graphql-client";
import {
  AppointmentFormData,
  BillingFormData,
  PatientFormData,
} from "~/pages/appointments/types";
import { DuplicatedFile } from "~/pydantic_models/lambdas/models/google_doc";
import { AuthUserGroup } from "~/types/auth";
import { performAutoAssignMA } from "~/utils/autoAssignMA";
import { getOnsiteOpIdForAppointment } from "~/utils/onsiteOpsAutoAssign";
import { determineDefaultOpsRoles } from "~/utils/opsRoleDefaults";
import { applyV2BriefingVersion } from "./appointment-v2-version";
import type { RestoredMedicalAssistant } from "./appointmentApiAdapter";
import {
  apiCancelReschedule,
  apiConfirmReschedule,
  apiCreateAppointmentRecord,
  apiCreateFromExistingAppointment,
  apiRescheduleFromExisting,
  apiRestore,
  apiRestoreInquiry,
  apiRevertToInquiry,
  apiScheduleAppointmentFromInquiry,
  apiStartRescheduling,
} from "./appointmentApiAdapter";
import { doctorService } from "./doctor";
import { fieldTemplateService } from "./fieldTemplate";
import {
  buildAddressInput,
  buildAttorneyInput,
  buildCCPersonInput,
  buildContactInput,
  buildContactUnavailabilityInput,
  buildCreatePatientCaseInput,
  buildCreatePatientCaseInputFromExistingPatientCaseInput,
  buildCreatePatientInputFromExistingPatientInput,
  buildEmploymentAtInjuryInput,
  buildInjuryInfoInput,
  buildNurseCaseManagerInput,
  buildScheduledCommsSuppressedParties,
  buildWorkersCompInsuranceInput,
  sanitizeAdditionalTimeslots,
  sanitizeCcEmails,
} from "./helpers/appointment-query-builder";
import {
  hasFieldUpdates,
  pruneUnchangedFields,
} from "./helpers/appointmentUpdateDiff";
import { createNotesByType } from "./notes";
import { uploadedFileService } from "./uploadedFile";

/**
 * Parameters for searching appointments with role-based access control
 */
export interface AppointmentSearchParams {
  /** The search query string to match against appointment fields (patient names, claim numbers, etc.) */
  query: string;
  /** Boolean flag to filter by production environment (true for prod, false for dev/staging) */
  isProd: boolean;
  /** Optional user role for access control. When set to DOCTOR, additional filtering may be applied */
  userGroup?: AuthUserGroup | null;
  /** Optional doctor ID for filtering results. Only used when userGroup is DOCTOR */
  doctorId?: string | null;
  /** Optional flag to filter by demo environment. Demo users see only demo appointments, non-demo users see only non-demo appointments */
  isDemo?: boolean;
}

export interface AddressManagementIdsInput {
  insuranceCarrierId: string | null;
  insuranceCarrierAddressId: string | null;
  insuranceCarrierFaxNumberId?: string | null;
  insuranceCarrierPhoneNumberId?: string | null;
  applicantAttorneyLawFirmId: string | null;
  applicantAttorneyAddressId: string | null;
  applicantAttorneyFaxNumberId?: string | null;
  applicantAttorneyPhoneNumberId?: string | null;
  defenseAttorneyLawFirmId: string | null;
  defenseAttorneyAddressId: string | null;
  defenseAttorneyFaxNumberId?: string | null;
  defenseAttorneyPhoneNumberId?: string | null;
}

/**
 * Outcome of `processAppointmentSubmission`. The appointment commit and the
 * post-commit note sync succeed independently: the note sync runs after the
 * appointment is already durable, so a failure there is reported as a partial
 * success rather than taking the id down with it (dropping the id is what let a
 * retry create a duplicate appointment).
 */
export interface AppointmentSubmissionResult {
  /** Id of the committed appointment; null/undefined when the commit produced none. */
  appointmentID: string | null | undefined;
  /**
   * True when the appointment saved but the edits/deletions of already-saved
   * notes did not. Callers surface this to the user — the note text only exists
   * in their browser, so nothing else can recover it.
   */
  noteSyncFailed: boolean;
}

interface HoldAdditionalTimeslot {
  date?: string | null;
  time?: string | null;
  locationID?: string | null;
}

type HoldPair = {
  sourceAppointment: AppointmentRecord;
  holdAppointment: AppointmentRecord;
};

const INCREMENT_GLOBAL_BILLING_COUNTER_MUTATION = /* GraphQL */ `
  mutation IncrementGlobalBillingCounterAtomic {
    incrementGlobalBillingCounterAtomic {
      id
      count
    }
  }
`;

const normalizeNullableString = (
  value: string | null | undefined,
): string | null => {
  if (typeof value !== "string") {
    return null;
  }

  const trimmedValue = value.trim();
  return trimmedValue.length > 0 ? trimmedValue : null;
};

/** Digits masked out, so a shape like `####-#` is diagnosable without the date
 *  itself leaving the browser. */
const dateShape = (value: string): string => value.replace(/\d/g, "#");

/**
 * Report a `{value, precision}` pair that names a value but not a real date, so
 * it can't be stored (spec §Observability, `partial_date.invalid_value`).
 *
 * A pair with a *null* value is the legitimate in-progress carrier — the MA
 * picked "Month" and hasn't typed one yet — and is silently dropped, not
 * reported. What this catches is ingest or a hand-rolled write producing
 * something like `{"2019-5", MONTH}`, which today vanishes with no trace.
 *
 * The report carries the field, the precision, and the value's *shape* — never
 * the value. A date is quasi-identifying and Sentry is outside the BAA boundary;
 * `####-#` says everything needed to spot a grammar mismatch. Exported so that
 * property can be tested.
 */
export const reportIncoherentDate = (
  field: string,
  pair: FlexibleDateInput | null,
): void => {
  if (!pair?.value || toStoredPair(pair)) return;
  errorReporter.reportMessage("flexible date dropped: value not coherent", {
    level: "warning",
    tags: { feature: "flexible-dates", field, precision: pair.precision },
    extra: { valueShape: dateShape(pair.value) },
  });
};

/** The four columns an employment date pair writes (GET-910). Returned as one
 *  object so a write site can't move half a pair; legacy is DAY-only. */
const employmentDates = (
  hire: FlexibleDateInput | null,
  termination: FlexibleDateInput | null,
) => {
  reportIncoherentDate("EmploymentRecord.dateOfHire", hire);
  reportIncoherentDate("EmploymentRecord.dateOfTermination", termination);
  return {
    dateOfHire: toDayIso(hire?.value ?? null, hire?.precision),
    dateOfHireFlexible: toStoredPair(hire),
    dateOfTermination: toDayIso(
      termination?.value ?? null,
      termination?.precision,
    ),
    dateOfTerminationFlexible: toStoredPair(termination),
  };
};

const sanitizeHoldAdditionalTimeslots = (
  additionalTimeslots: HoldAdditionalTimeslot[] | undefined,
): Array<{ date: string; time: string; locationID?: string | null }> | null => {
  const normalizedSlots = (additionalTimeslots ?? [])
    .map((timeslot) => ({
      date: normalizeNullableString(timeslot?.date),
      time: normalizeNullableString(timeslot?.time),
      locationID: normalizeNullableId(timeslot?.locationID),
    }))
    .filter(
      (
        timeslot,
      ): timeslot is {
        date: string;
        time: string;
        locationID: string | null;
      } => Boolean(timeslot.date && timeslot.time),
    );

  return normalizedSlots.length > 0 ? normalizedSlots : null;
};

const HOLD_SOURCE_NOTE_PREFIX =
  "This appointment is in the process of being moved.";
const HOLD_TARGET_NOTE_PREFIX =
  "An appointment is in the process of being moved to this time.";

const resolveReevaluationType = (
  type: AppointmentType | null | undefined,
  s3FileKey?: string | null,
): AppointmentType | undefined => {
  if (
    type === AppointmentType.QME ||
    type === AppointmentType.QME_REEVALUATION
  ) {
    return AppointmentType.QME_REEVALUATION;
  }
  if (
    type === AppointmentType.AME ||
    type === AppointmentType.AME_REEVALUATION
  ) {
    return AppointmentType.AME_REEVALUATION;
  }
  if (
    type === AppointmentType.SIBTF ||
    type === AppointmentType.SIBTF_REEVALUATION
  ) {
    return AppointmentType.SIBTF_REEVALUATION;
  }
  if (
    type === AppointmentType.IME ||
    type === AppointmentType.IME_REEVALUATION
  ) {
    return AppointmentType.IME_REEVALUATION;
  }
  if (
    type === AppointmentType.IME_ADR ||
    type === AppointmentType.IME_ADR_REEVALUATION
  ) {
    return AppointmentType.IME_ADR_REEVALUATION;
  }
  return getReevaluationTypeByFileKey(s3FileKey) || undefined;
};

/**
 * Stored-side lookups for `pruneUnchangedFields`, taken from the record a form
 * was hydrated from rather than the caller's live one.
 *
 * The two diverge whenever a tab freezes its form while editing but keeps
 * receiving subscription updates, which is every migrated tab. Diffing against
 * the live record reads the frozen form's stale nulls as deliberate clears —
 * the wipe the diff exists to prevent. A case absent from the source (created
 * after hydration) resolves to `null`, which prunes nothing and leaves the
 * pre-diff behavior.
 */
function hydrationBaseline(source: AppointmentRecord | null) {
  const cases = source ? sortPatientCases(source) : [];
  const casesById = new Map(
    cases.flatMap((entry) =>
      entry.patientCase ? [[entry.patientCase.id, entry.patientCase]] : [],
    ),
  );

  return {
    appointment: source,
    patient: cases[0]?.patientCase?.patient ?? null,
    patientCase: (id: string) => casesById.get(id) ?? null,
  };
}

export class AppointmentService {
  private static instance: AppointmentService;
  private readonly client: GraphQLClientWrapper;

  private constructor(client: GraphQLClientWrapper) {
    this.client = client;
  }

  static getInstance(client?: GraphQLClientWrapper): AppointmentService {
    if (!AppointmentService.instance) {
      AppointmentService.instance = new AppointmentService(
        client || graphqlClient,
      );
    }
    return AppointmentService.instance;
  }

  private isReschedulingHoldStatus(
    status: AppointmentStatus | null | undefined,
  ): boolean {
    return (
      status === AppointmentStatus.RESCHEDULING_HOLD ||
      status === AppointmentStatus.LATE_RESCHEDULING_HOLD
    );
  }

  private isReschedulingInProgressStatus(
    status: AppointmentStatus | null | undefined,
  ): boolean {
    return status === AppointmentStatus.RESCHEDULING_IN_PROGRESS;
  }

  private async resolveRescheduleHoldPair(
    appointment: AppointmentRecord,
    userGroup?: AuthUserGroup,
  ): Promise<HoldPair> {
    if (this.isReschedulingHoldStatus(appointment.status)) {
      const sourceId = appointment.rescheduledFromAppointmentID;
      if (!sourceId) {
        throw new Error("Hold appointment has no source appointment link");
      }

      const sourceAppointment = await this.fetchAppointmentById(
        sourceId,
        userGroup,
      );
      if (!sourceAppointment) {
        throw new Error("Source appointment not found for hold confirmation");
      }

      return {
        sourceAppointment,
        holdAppointment: appointment,
      };
    }

    if (this.isReschedulingInProgressStatus(appointment.status)) {
      const holdId = appointment.rescheduledToAppointmentID;
      if (!holdId) {
        throw new Error("In-progress appointment has no hold appointment link");
      }

      const holdAppointment = await this.fetchAppointmentById(
        holdId,
        userGroup,
      );
      if (!holdAppointment) {
        throw new Error("Hold appointment not found for hold confirmation");
      }

      if (!this.isReschedulingHoldStatus(holdAppointment.status)) {
        throw new Error(
          "Linked appointment is not in rescheduling hold status",
        );
      }

      return {
        sourceAppointment: appointment,
        holdAppointment,
      };
    }

    throw new Error("Appointment is not in hold/in-progress rescheduling flow");
  }

  private formatParticipantType(value: ParticipantType): string {
    return toLowerCase(value);
  }

  private formatHoldRequesterLabel(params: {
    requestedBy: ParticipantType | null;
    requestedInternally: boolean;
    requesterDisplayName?: string;
  }): string {
    const normalizedRequesterName = normalizeNullableString(
      params.requesterDisplayName,
    );
    if (normalizedRequesterName) {
      return normalizedRequesterName;
    }

    if (params.requestedInternally) {
      return "Internal staff";
    }

    if (params.requestedBy) {
      return this.formatParticipantType(params.requestedBy);
    }

    return "Unknown";
  }

  private formatHoldReasonLabel(reason: ReschedulingReason): string {
    return toSentenceCase(reason);
  }

  private getAppointmentRoute(appointmentId: string): string {
    return `/appointments/${appointmentId}`;
  }

  private buildHoldSourceSystemNote(params: {
    reason: ReschedulingReason;
    requesterLabel: string;
    holdAppointmentId: string;
  }): string {
    return `${HOLD_SOURCE_NOTE_PREFIX} Rescheduling reason: ${this.formatHoldReasonLabel(params.reason)}. Requester: ${params.requesterLabel}. See new appointment with proposed time: ${this.getAppointmentRoute(params.holdAppointmentId)}.`;
  }

  private buildHoldTargetSystemNote(params: {
    reason: ReschedulingReason;
    requesterLabel: string;
    sourceAppointmentId: string;
  }): string {
    return `${HOLD_TARGET_NOTE_PREFIX} Rescheduling reason: ${this.formatHoldReasonLabel(params.reason)}. Requester: ${params.requesterLabel}. See old appointment: ${this.getAppointmentRoute(params.sourceAppointmentId)}.`;
  }

  /**
   * Resolves a reschedule-hold pair from either side of the relationship.
   *
   * Why this exists:
   * - UI actions can be initiated from either the source appointment
   *   (`RESCHEDULING_IN_PROGRESS`) or the hold appointment
   *   (`RESCHEDULING_HOLD`/`LATE_RESCHEDULING_HOLD`).
   * - Centralizing this lookup in the service keeps linking rules consistent
   *   and avoids duplicated pair-resolution logic in components.
   */
  async getRescheduleHoldPair(
    appointment: AppointmentRecord,
    userGroup?: AuthUserGroup,
  ): Promise<HoldPair> {
    return this.resolveRescheduleHoldPair(appointment, userGroup);
  }

  /**
   * Phase 2: Get fieldTemplateRef input for appointment creation.
   * Returns an object with fieldTemplateRef if the doctor has a FieldTemplate,
   * or an empty object if not (to allow spreading into mutation input).
   */
  private async getFieldTemplateRefInput(
    doctorId?: string,
  ): Promise<{ fieldTemplateRef: { id: string; version: number } } | object> {
    if (!doctorId) return {};

    try {
      const templateRef =
        await fieldTemplateService.getTemplateRefForDoctor(doctorId);
      if (templateRef) {
        return { fieldTemplateRef: templateRef };
      }
    } catch (error) {
      console.warn(
        "Failed to get doctor template ref, using embedded fields only:",
        error,
      );
    }
    return {};
  }

  /**
   * Updates an existing appointment record and related entities
   * @param appointment The existing appointment record to update
   * @param formData The form data containing all appointment information
   * @param user The current user
   * @param noInterpreterNeeded Flag indicating if interpreter is needed
   * @param isDemo Flag indicating if the appointment belongs to demo environment (demo users only see demo appointments)
   * @param targetAppointmentStatus Optional new appointment status
   * @returns The ID of the newly created appointment record
   */
  async createAppointmentRecord(
    formData: AppointmentFormData,
    user: User | null,
    noInterpreterNeeded: boolean,
    isDemo = false,
    targetAppointmentStatus?: AppointmentStatus,
    addressManagementIds?: AddressManagementIdsInput,
    // When creating an appointment from an existing case, link it to that
    // CaseRecord so it joins the case's chain. New patient/case rows are still
    // created from the form data, mirroring the backend clone behavior.
    caseID?: string | null,
    // Intake files staged in this session. The create-scheduled endpoint copies
    // them server-side as part of its post-commit doc setup, so the caller must
    // NOT also invoke the doc lambda (W1-05).
    intakeFormS3Keys?: string[],
    flexibleInjuryDatesEnabled = false,
  ) {
    return apiCreateAppointmentRecord({
      formData,
      user,
      noInterpreterNeeded,
      targetAppointmentStatus,
      addressManagementIds,
      isDemo,
      caseID,
      intakeFormS3Keys,
      flexibleInjuryDatesEnabled,
    });
  }

  /**
   * Schedules an appointment from an existing inquiry appointment
   * @param appointment The existing appointment record to schedule
   * @param formData The form data containing all appointment information
   * @param user The current user
   * @param noInterpreterNeeded Flag indicating if interpreter is needed
   * @returns Promise resolving when the scheduling completes
   */
  async scheduleAppointmentFromInquiry(
    appointment: AppointmentRecord,
    formData: AppointmentFormData,
    user: User | null,
    noInterpreterNeeded: boolean,
    addressManagementIds?: AddressManagementIdsInput,
    // Intake files staged in this session — the endpoint copies them
    // server-side, so the caller must not also invoke the doc lambda (W1-05).
    intakeFormS3Keys?: string[],
    flexibleInjuryDatesEnabled = false,
  ): Promise<void> {
    return apiScheduleAppointmentFromInquiry({
      appointment,
      formData,
      user,
      noInterpreterNeeded,
      addressManagementIds,
      intakeFormS3Keys,
      flexibleInjuryDatesEnabled,
    });
  }

  /**
   * Reverts a SCHEDULED appointment back to INQUIRY. The slot (date/time/doctor)
   * is intentionally kept — this only flips `status`, matching the legacy
   * `handleOpenRevertToInquiryModal` inline mutation.
   *
   * Served by the ECS `POST /{id}/revert-to-inquiry` endpoint (direct-DDB,
   * already attributed, zero parity gaps).
   * @param appointment The scheduled appointment to revert
   * @returns Promise resolving when the revert completes
   */
  async revertToInquiry(appointment: AppointmentRecord): Promise<void> {
    return apiRevertToInquiry({ appointment });
  }

  /**
   * Restores a cancelled appointment, clearing the `cancellation*` fields. The
   * target status (SCHEDULED, or DEPOSITION_HOLD for a hold that kept its
   * alternate slots) is resolved server-side from the row itself.
   *
   * Served by the ECS `POST /{id}/restore` endpoint, which also re-validates
   * the MA the row kept through the cancellation — the reason this is not the
   * direct AppSync mutation it used to be.
   * @param appointmentId The cancelled appointment to restore
   * @returns Where the inherited-MA re-check left the appointment, so the
   * caller can report a reassignment the operator did not choose; null
   * when the server did not report one
   */
  async restore(
    appointmentId: string,
  ): Promise<RestoredMedicalAssistant | null> {
    return apiRestore({ appointmentId });
  }

  /**
   * Reopens a closed inquiry (INQUIRY_CLOSED → INQUIRY), clearing the
   * `inquiryClosing*` fields. Inverse of close-inquiry.
   *
   * Served by the ECS `POST /{id}/restore-inquiry` endpoint.
   * @param appointmentId The closed inquiry to reopen
   * @returns Promise resolving when the restore completes
   */
  async restoreInquiry(appointmentId: string): Promise<void> {
    return apiRestoreInquiry({ appointmentId });
  }

  /**
   * Reverts a scheduled re-evaluation back to REEVAL_HOLD — the re-eval
   * analog of revertToInquiry. Always the direct AppSync mutation: the ECS
   * revert endpoint hardcodes INQUIRY (hold support is Phase 3).
   */
  async revertToReevalHold(appointment: AppointmentRecord): Promise<void> {
    await this.writeStatus(appointment, AppointmentStatus.REEVAL_HOLD);
  }

  /** Bare status flip — the shared write behind the revert paths. */
  private async writeStatus(
    appointment: AppointmentRecord,
    status: AppointmentStatus,
  ): Promise<void> {
    await this.client.graphql({
      query: updateAppointmentRecordReturningID,
      variables: {
        input: {
          id: appointment.id,
          status,
        },
      },
    });
  }

  /**
   * Updates an existing appointment record and related entities
   * @param appointment The existing appointment record to update
   * @param formData The form data containing all appointment information
   * @param user The current user
   * @param noInterpreterNeeded Flag indicating if interpreter is needed
   * @param hydrationSource The record `formData` was hydrated from — the diff
   *   baseline, which is NOT necessarily `appointment`. See the parameter's
   *   own comment below.
   * @param appointmentStatus Optional new appointment status
   * @returns Promise resolving when the update completes
   */
  async saveAppointmentDetails(
    appointment: AppointmentRecord,
    formData: AppointmentFormData,
    user: User | null,
    noInterpreterNeeded: boolean,
    /**
     * The record `formData` was hydrated from — the diff baseline, and NOT
     * the same thing as `appointment`. The tab's form is frozen while editing
     * but its `appointment` prop keeps refreshing from the subscription, so
     * diffing against the live record would read the form's stale nulls as
     * deliberate clears and re-open the wipe this diff exists to close.
     * `null` when the form was never hydrated — then nothing is pruned, which
     * is the pre-diff behavior.
     */
    hydrationSource: AppointmentRecord | null,
    appointmentStatus?: AppointmentStatus,
    s3FileKey?: string,
    selectedDoctor?: Doctor,
    addressManagementIds?: AddressManagementIdsInput,
    flexibleInjuryDatesEnabled = false,
  ): Promise<void> {
    // Use the same logic as other parts of the app for better fallback handling
    const sortedPatientCases = sortPatientCases(appointment);
    const primaryPatientCase = sortedPatientCases[0]?.patientCase;
    const otherCases = sortedPatientCases.slice(1); // All cases except the first (primary)

    if (!primaryPatientCase) {
      throw new InternalError("No valid primary case found");
    }

    const patientID = primaryPatientCase.patientID;

    if (!patientID) {
      throw new InternalError("Patient ID is missing");
    }

    // Changing physician is only allowed in Inquiry stage
    if (
      appointment.status &&
      appointment.status !== AppointmentStatus.INQUIRY &&
      appointment.doctorID !==
        formData.tentativeAppointment.content.preferredPhysician
    ) {
      throw new UserError(
        "Cannot change physician once an appointment is scheduled",
      );
    }

    const patientInfo = formData.patientInfo.content;
    const appointmentInfo = formData.tentativeAppointment.content;
    const adjusterInfo = formData.adjusterInfo.content;
    const nurseCaseManager = formData.nurseCaseManagerInfo.content;
    const applicantAttorney = formData.applicantAttorneyInfo.content;
    const defenseAttorney = formData.defenseAttorneyInfo.content;
    const carbonCopy = formData.carbonCopyInfo.content;
    const notes = formData.notes.content;

    try {
      const baseline = hydrationBaseline(hydrationSource);

      const updatePatientInput = pruneUnchangedFields(
        {
          id: patientID,
          name: patientInfo.patientName || null,
          dateOfBirth: patientInfo.dateOfBirth?.toString() || null,
          SSN: patientInfo.SSN || null,
          language: patientInfo.language || null,
          dominantHand: patientInfo.dominantHand || null,
          contact: {
            email: patientInfo.email || null,
            phone: patientInfo.cellPhoneNumber || null,
            homePhoneNumber: patientInfo.homePhoneNumber || null,
            address: {
              street: patientInfo.homeMailingAddress.street || null,
              city: patientInfo.homeMailingAddress.city || null,
              state: patientInfo.homeMailingAddress.state || null,
              zip: patientInfo.homeMailingAddress.zipcode || null,
            },
          },
        },
        baseline.patient,
      );
      const updatePatientPromise = hasFieldUpdates(updatePatientInput)
        ? this.client.graphql({
            query: updatePatientReturningID,
            variables: { input: updatePatientInput },
          })
        : null;

      if (!appointment.patientCases?.items) {
        throw new InternalError("Patient cases are missing");
      }

      // Flag to track if we need to update primaryPatientCaseID
      // This would be true if we're using a fallback primary case (different from the appointment's primaryPatientCaseID)
      const shouldUpdatePrimaryCase =
        appointment.primaryPatientCaseID !== primaryPatientCase.id;

      const appointmentPatientCasePairsToDeletePromises: GraphQLResponseV6[] =
        [];
      const patientCasesToDeletePromises: GraphQLResponseV6[] = [];
      const newPatientCasePromises: GraphQLResponseV6[] = [];
      const updatePatientCasePromises: GraphQLResponseV6[] = [];

      // Always update primary case with first claim info
      const primaryCaseInput = pruneUnchangedFields(
        {
          id: primaryPatientCase.id,
          patientID,
          // Note that this is not protected by type as this is an update query, but `null` will actually be saved as NULL in the DB
          // TODO(@jasonduochen/@AndriiHrimov/@isainkov-nix): figure out how to strictly type this
          claimNumber: formData.claimInfo.content[0].claimNumber || "",
          qmePanelNumber: formData.claimInfo.content[0].panelNumber,
          WCAB: formData.claimInfo.content[0].WCAB,
          contact: buildContactInput({
            phone: formData.claimInfo.content[0].employerTelephoneNumber,
            phoneExtension:
              formData.claimInfo.content[0].employerTelephoneNumberExtension,
            address: buildAddressInput(
              formData.claimInfo.content[0].companyAddress,
            ),
          }),
          employmentAtInjury: buildEmploymentAtInjuryInput(
            formData.claimInfo.content[0],
          ),
          injuryInfo: buildInjuryInfoInput(
            formData.claimInfo.content[0],
            flexibleInjuryDatesEnabled,
          ),
          workersCompInsurance: buildWorkersCompInsuranceInput(adjusterInfo),
          applicantAttorney: buildAttorneyInput(
            applicantAttorney,
            "applicantAttorney",
          ),
          defenseAttorney: buildAttorneyInput(
            defenseAttorney,
            "defenseAttorney",
          ),
          nurseCaseManager: buildNurseCaseManagerInput(nurseCaseManager),
          ccPerson: buildCCPersonInput(carbonCopy),
          adjusterCcEmails: sanitizeCcEmails(adjusterInfo.adjusterCcEmails),
          applicantAttorneyCcEmails: sanitizeCcEmails(
            applicantAttorney.applicantAttorneyCcEmails,
          ),
          defenseAttorneyCcEmails: sanitizeCcEmails(
            defenseAttorney.defenseAttorneyCcEmails,
          ),
          scheduledCommsSuppressedParties: buildScheduledCommsSuppressedParties(
            patientInfo,
            adjusterInfo,
            applicantAttorney,
            defenseAttorney,
          ),
          // Add address management IDs if provided
          ...(addressManagementIds && {
            insuranceCarrierID: addressManagementIds.insuranceCarrierId || null,
            insuranceCarrierAddressID:
              addressManagementIds.insuranceCarrierAddressId || null,
            insuranceCarrierFaxNumberID:
              addressManagementIds.insuranceCarrierFaxNumberId || null,
            insuranceCarrierPhoneNumberID:
              addressManagementIds.insuranceCarrierPhoneNumberId || null,
            applicantAttorneyLawFirmID:
              addressManagementIds.applicantAttorneyLawFirmId || null,
            applicantAttorneyAddressID:
              addressManagementIds.applicantAttorneyAddressId || null,
            applicantAttorneyFaxNumberID:
              addressManagementIds.applicantAttorneyFaxNumberId || null,
            applicantAttorneyPhoneNumberID:
              addressManagementIds.applicantAttorneyPhoneNumberId || null,
            defenseAttorneyLawFirmID:
              addressManagementIds.defenseAttorneyLawFirmId || null,
            defenseAttorneyAddressID:
              addressManagementIds.defenseAttorneyAddressId || null,
            defenseAttorneyFaxNumberID:
              addressManagementIds.defenseAttorneyFaxNumberId || null,
            defenseAttorneyPhoneNumberID:
              addressManagementIds.defenseAttorneyPhoneNumberId || null,
          }),
        },
        baseline.patientCase(primaryPatientCase.id),
      );

      if (hasFieldUpdates(primaryCaseInput)) {
        updatePatientCasePromises.push(
          this.client.graphql({
            query: updatePatientCaseReturningID,
            variables: { input: primaryCaseInput },
          }),
        );
      }

      // Handle remaining cases (starting from index 1)
      const remainingFormCases = formData.claimInfo.content.slice(1);

      // Update existing cases or create new ones
      for (let i = 0; i < remainingFormCases.length; i++) {
        const caseInfo = remainingFormCases[i];
        const existingCase = otherCases[i];

        if (existingCase) {
          // Update existing case
          const existingCaseInput = pruneUnchangedFields(
            {
              ...buildCreatePatientCaseInput(
                patientID,
                caseInfo,
                adjusterInfo,
                applicantAttorney,
                defenseAttorney,
                nurseCaseManager,
                carbonCopy,
                patientInfo,
                undefined,
                flexibleInjuryDatesEnabled,
              ),
              id: existingCase.patientCase.id,
            },
            baseline.patientCase(existingCase.patientCase.id),
          );
          if (hasFieldUpdates(existingCaseInput)) {
            updatePatientCasePromises.push(
              this.client.graphql({
                query: updatePatientCaseReturningID,
                variables: { input: existingCaseInput },
              }),
            );
          }
        } else {
          // Create new case
          const createPatientCasePromise = this.client.graphql({
            query: createPatientCaseReturningID,
            variables: {
              input: buildCreatePatientCaseInput(
                patientID,
                caseInfo,
                adjusterInfo,
                applicantAttorney,
                defenseAttorney,
                nurseCaseManager,
                carbonCopy,
                patientInfo,
                undefined,
                flexibleInjuryDatesEnabled,
              ),
            },
          });
          newPatientCasePromises.push(createPatientCasePromise);
        }
      }

      // Delete any remaining old cases
      for (let i = remainingFormCases.length; i < otherCases.length; i++) {
        const casePair = otherCases[i];

        const deleteMappingPromise = this.client.graphql({
          query: deleteAppointmentRecordPatientCaseReturningID,
          variables: { input: { id: casePair.id } },
        });
        appointmentPatientCasePairsToDeletePromises.push(deleteMappingPromise);

        const deletePatientCasePromise = this.client.graphql({
          query: deletePatientCaseReturningID,
          variables: { input: { id: casePair.patientCaseId } },
        });
        patientCasesToDeletePromises.push(deletePatientCasePromise);
      }

      // Handle new case mappings
      const newPatientCaseResponses = await Promise.all(newPatientCasePromises);
      const newPatientCaseIDs = newPatientCaseResponses.map(
        (response) =>
          (response as { data: { createPatientCase: { id: string } } }).data
            .createPatientCase.id,
      );

      const appointmentPatientCasePairsToCreatePromises = newPatientCaseIDs.map(
        (patientCaseID: string) =>
          this.client.graphql({
            query: createAppointmentRecordPatientCaseReturningPairID,
            variables: {
              input: {
                appointmentRecordId: appointment.id,
                patientCaseId: patientCaseID,
              },
            },
          }),
      );

      const appointment_date = appointmentInfo.dateTime.date
        ? appointmentInfo.dateTime.date.toString()
        : DEFAULT_DATE_ISO;

      const appointmentDetailsInput: UpdateAppointmentRecordInput = {
        id: appointment.id,
        ...(shouldUpdatePrimaryCase
          ? {
              primaryPatientCaseID: primaryPatientCase.id,
            }
          : {}),
        date: appointment_date,
        yearMonth: extractYearMonthFromDateISO(appointment_date),
        doctorYearMonth: generateDoctorYearMonths(
          appointmentInfo.preferredPhysician,
          appointment_date,
        ),
        time: appointmentInfo.dateTime.time || DEFAULT_TIME_ISO,
        additionalTimeslots: sanitizeAdditionalTimeslots(
          appointmentInfo.additionalTimeslots,
          appointmentInfo.preferredLocation,
        ),
        inquiryDate:
          formData.inquirerInfo.content.date?.toString() || DEFAULT_DATE_ISO,
        inquiryYearMonth: extractYearMonthFromDateISO(
          formData.inquirerInfo.content.date?.toString() || DEFAULT_DATE_ISO,
        ),
        type: appointmentInfo.appointmentType || null,
        s3FileKey: s3FileKey || undefined,
        doctorID: appointmentInfo.preferredPhysician || null,
        locationID: appointmentInfo.preferredLocation || null,
        // `active` and `paymentStatus` are creation defaults and belong only to
        // the create path (see `createAppointmentRecord` / the ECS creator).
        // The form exposes no control for either, so a Save carries no user
        // intent about them — writing them back reactivates a deactivated
        // appointment and rewinds payment to PENDING, and the diff cannot mask
        // it because a differing stored value is exactly what counts as a
        // change. They are absent here so Save can never touch them.
        ...(appointmentStatus ? { status: appointmentStatus } : {}),
        scheduler: {
          name: appointmentInfo.personToCall,
          organizationName: appointmentInfo.organizationName,
          contact: {
            phone: appointmentInfo.phoneNumber,
            phoneExtension: appointmentInfo.phoneNumberExtension,
            email: appointmentInfo.email || null,
            address: {
              street: "",
              city: "",
              state: "",
              zip: "",
            },
            fax: "",
          },
        },
        durationMinutes: appointmentInfo.durationMinutes,
        appointmentRecordMedicalAssistantId: appointmentInfo.medicalAssistant,
        appointmentRecordSchedulingAssistantId:
          appointmentInfo.schedulingAssistant,
        appointmentRecordOnSiteOpId: appointmentInfo.onSiteOp,
        appointmentRecordReportOpId: appointmentInfo.reportOp ?? null,
        appointmentRecordBillingOpId: appointmentInfo.billingOp ?? null,
        appointmentRecordPreVisitReviewerId:
          appointmentInfo.preVisitReviewer ?? null,
        panelSpecialty: appointmentInfo.panelSpecialty,
        inquirerInfo: {
          name: formData.inquirerInfo.content.name || null,
          inquiringParty: formData.inquirerInfo.content.party || null,
          contact: {
            email: formData.inquirerInfo.content.email || null,
            phone: formData.inquirerInfo.content.phoneNumber,
            phoneExtension: formData.inquirerInfo.content.phoneNumberExtension,
          },
          notification_110_required:
            formData.inquirerInfo.content.notification_110_required,
        },
        interpreter: {
          ...(noInterpreterNeeded === false
            ? {
                companyName: appointmentInfo.interpreterCompanyName,
                phoneNumber: appointmentInfo.interpreterPhoneNumber,
                phoneExtension: appointmentInfo.interpreterPhoneNumberExtension,
                note: appointmentInfo.interpreterNote,
                name: appointmentInfo.interpreterName,
                certificateId: appointmentInfo.interpreterCertificateID,
              }
            : {}),
        },
        videoCallLink: appointmentInfo.videoCallLink,
        defenseAttorneyNotPresent: Boolean(defenseAttorney.isNotPresent),
        applicantAttorneyNotPresent: Boolean(applicantAttorney.isNotPresent),
        contactUnavailability: buildContactUnavailabilityInput(
          formData,
          appointment.contactUnavailability ?? null,
        ),
      };

      // Set doctorOwnerCognitoId if doctor is assigned
      if (selectedDoctor && selectedDoctor.ownerCognitoId) {
        appointmentDetailsInput.doctorOwnerCognitoId =
          selectedDoctor.ownerCognitoId;
      }

      // On the INQUIRY→SCHEDULED transition, set fieldTemplateRef from the
      // doctor's latest FieldTemplate. The physician cannot change once an
      // appointment is scheduled (guarded above with a UserError), so this
      // transition is the only point a fresh ref needs resolving here. Existing
      // SCHEDULED appointments that are missing the ref are repaired by the
      // self-heal effect in PatientInfoTab.
      if (
        appointmentStatus === AppointmentStatus.SCHEDULED &&
        appointment.status !== appointmentStatus &&
        selectedDoctor
      ) {
        const templateRefInput = await this.getFieldTemplateRefInput(
          selectedDoctor.id,
        );
        Object.assign(appointmentDetailsInput, templateRefInput);
      }

      // Send only what this save actually changes. The payload above holds
      // every field on the form, so shipping it whole makes Save a blind
      // overwrite: a field the form failed to hydrate is written back as an
      // explicit null and the stored value is destroyed. Diffing against the
      // record the form was hydrated from means a field empty on both sides
      // never enters the mutation. See `appointmentUpdateDiff`.
      const changedAppointmentFields = pruneUnchangedFields(
        appointmentDetailsInput,
        hydrationSource,
      );
      const saveAppointmentDetailsPromise = hasFieldUpdates(
        changedAppointmentFields,
      )
        ? this.client.graphql({
            query: updateAppointmentRecordReturningID,
            variables: {
              input: changedAppointmentFields,
            },
          })
        : null;

      const existingNotesResponse = await this.client.graphql({
        query: notesByAppointmentrecordID,
        variables: { appointmentrecordID: appointment.id },
      });

      const existingNotes =
        existingNotesResponse.data.notesByAppointmentrecordID.items;
      const existingNotesMap = new Map<
        string,
        { content: string | null; timeStamp: number | null }
      >(
        existingNotes.map((note) => [
          note.id,
          {
            content: note.content || "",
            timeStamp:
              note.timeStamp !== null && note.timeStamp !== undefined
                ? note.timeStamp
                : -1,
          },
        ]),
      );

      const notesToCreatePromises = [];

      for (const note of notes) {
        let awsTimestamp: number;
        try {
          awsTimestamp = note.dateTime.getTime();

          if (isNaN(awsTimestamp)) {
            console.warn("Invalid note timestamp:", note.dateTime);
            continue;
          }
        } catch (err) {
          console.warn("Error parsing note timestamp:", err);
          continue;
        }

        const noteExists = Array.from(existingNotesMap.values()).some(
          (existingNote) =>
            existingNote.timeStamp === awsTimestamp &&
            existingNote.content === note.content,
        );

        if (!user) {
          console.warn("User not found, skipping note creation");
          continue;
        }

        if (!noteExists) {
          notesToCreatePromises.push(
            this.client.graphql({
              query: createNotesReturningID,
              variables: {
                input: {
                  content: note.content,
                  notesAuthorId: user.id,
                  timeStamp: awsTimestamp,
                  appointmentrecordID: appointment.id,
                  type: note.type,
                },
              },
            }),
          );
        }
      }

      await Promise.all([
        ...(updatePatientPromise ? [updatePatientPromise] : []),
        ...(saveAppointmentDetailsPromise
          ? [saveAppointmentDetailsPromise]
          : []),
        ...updatePatientCasePromises,
        ...notesToCreatePromises,
        ...appointmentPatientCasePairsToDeletePromises,
        ...appointmentPatientCasePairsToCreatePromises,
      ]);

      await Promise.all(patientCasesToDeletePromises);
    } catch (error) {
      console.error("Error handling submit update flow:", error);
    }
  }

  /**
   * Note: we should guarantee, that appointment will have patientCases (at least one), primaryCase and patient.
   * Based on this, we are casting to non-null values.
   */
  async updatePatientInfo(
    appointment: AppointmentRecord,
    formData: PatientFormData,
    user: User | null,
    /** The record `formData` was hydrated from — see `hydrationBaseline`. */
    hydrationSource: AppointmentRecord | null,
    flexibleInjuryDatesEnabled = false,
  ) {
    const baseline = hydrationBaseline(hydrationSource);
    // Use the same logic as other parts of the app for better fallback handling
    const sortedPatientCases = sortPatientCases(appointment);
    const primaryPatientCaseWrapper = sortedPatientCases[0];

    if (!primaryPatientCaseWrapper?.patientCase?.patient) {
      throw new InternalError(
        "No valid primary case found for patient info update",
      );
    }

    const patient = primaryPatientCaseWrapper.patientCase.patient as Patient;
    const patientID = primaryPatientCaseWrapper.patientCase.patientID;
    const otherCases = sortedPatientCases.slice(1);

    const updatePatientInput = pruneUnchangedFields(
      {
        id: patient.id,
        name: formData.identification.content.name,
        sex: formData.identification.content.sex || null,
        occupation: formData.identification.content.occupation,
        weightLb: formData.identification.content.weight,
        height: {
          feet: formData.identification.content.heightFeet,
          inches: formData.identification.content.heightInches,
        },
        dominantHand: formData.identification.content.dominantHand || null,
      },
      baseline.patient,
    );
    const updatePatientPromise = hasFieldUpdates(updatePatientInput)
      ? this.client.graphql({
          query: updatePatientReturningID,
          variables: { input: updatePatientInput },
        })
      : null;

    const claimFormData = formData.claimInfo?.content || [];
    const patientCaseUpdatePromises: Array<Promise<unknown>> = [];
    const newPatientCasePromises: Array<Promise<unknown>> = [];
    const appointmentPatientCasePairsToDeletePromises: Array<Promise<unknown>> =
      [];
    const patientCasesToDeletePromises: Array<Promise<unknown>> = [];
    let appointmentPatientCasePairsToCreatePromises: Array<Promise<unknown>> =
      [];

    if (claimFormData.length > 0) {
      const [primaryClaim, ...remainingClaims] = claimFormData;

      const primaryCaseInput = pruneUnchangedFields(
        {
          id: primaryPatientCaseWrapper.patientCase.id,
          claimNumber: primaryClaim.claimNumber || "",
          qmePanelNumber: primaryClaim.panelNumber,
          WCAB: primaryClaim.WCAB,
          contact: buildContactInput({
            phone: primaryClaim.employerTelephoneNumber,
            phoneExtension: primaryClaim.employerTelephoneNumberExtension,
            address: buildAddressInput(primaryClaim.companyAddress || {}),
          }),
          employmentAtInjury: buildEmploymentAtInjuryInput(primaryClaim),
          injuryInfo: buildInjuryInfoInput(
            primaryClaim,
            flexibleInjuryDatesEnabled,
          ),
        },
        baseline.patientCase(primaryPatientCaseWrapper.patientCase.id),
      );
      if (hasFieldUpdates(primaryCaseInput)) {
        patientCaseUpdatePromises.push(
          this.client.graphql({
            query: updatePatientCaseReturningID,
            variables: { input: primaryCaseInput },
          }),
        );
      }

      for (let i = 0; i < remainingClaims.length; i++) {
        const claim = remainingClaims[i];
        const existingCase = otherCases[i];

        if (existingCase) {
          const existingCaseInput = pruneUnchangedFields(
            {
              id: existingCase.patientCase.id,
              claimNumber: claim.claimNumber || "",
              qmePanelNumber: claim.panelNumber,
              WCAB: claim.WCAB,
              contact: buildContactInput({
                phone: claim.employerTelephoneNumber,
                phoneExtension: claim.employerTelephoneNumberExtension,
                address: buildAddressInput(claim.companyAddress || {}),
              }),
              employmentAtInjury: buildEmploymentAtInjuryInput(claim),
              injuryInfo: buildInjuryInfoInput(
                claim,
                flexibleInjuryDatesEnabled,
              ),
            },
            baseline.patientCase(existingCase.patientCase.id),
          );
          if (hasFieldUpdates(existingCaseInput)) {
            patientCaseUpdatePromises.push(
              this.client.graphql({
                query: updatePatientCaseReturningID,
                variables: { input: existingCaseInput },
              }),
            );
          }
        } else {
          const baseInput =
            buildCreatePatientCaseInputFromExistingPatientCaseInput(
              primaryPatientCaseWrapper,
              patientID,
            );
          const newPatientCaseInput = {
            ...baseInput,
            claimNumber: claim.claimNumber || "",
            qmePanelNumber: claim.panelNumber,
            WCAB: claim.WCAB,
            contact: buildContactInput({
              phone: claim.employerTelephoneNumber,
              phoneExtension: claim.employerTelephoneNumberExtension,
              address: buildAddressInput(claim.companyAddress || {}),
            }),
            employmentAtInjury: buildEmploymentAtInjuryInput(claim),
            injuryInfo: buildInjuryInfoInput(claim, flexibleInjuryDatesEnabled),
          };

          newPatientCasePromises.push(
            this.client.graphql({
              query: createPatientCaseReturningID,
              variables: {
                input: newPatientCaseInput,
              },
            }),
          );
        }
      }

      for (let i = remainingClaims.length; i < otherCases.length; i++) {
        const casePair = otherCases[i];

        appointmentPatientCasePairsToDeletePromises.push(
          this.client.graphql({
            query: deleteAppointmentRecordPatientCaseReturningID,
            variables: { input: { id: casePair.id } },
          }),
        );

        patientCasesToDeletePromises.push(
          this.client.graphql({
            query: deletePatientCaseReturningID,
            variables: { input: { id: casePair.patientCaseId } },
          }),
        );
      }

      if (newPatientCasePromises.length > 0) {
        const newPatientCaseResponses = await Promise.all(
          newPatientCasePromises,
        );
        const newPatientCaseIDs = newPatientCaseResponses.map((response) => {
          const typedResponse = response as {
            data: { createPatientCase: { id: string } };
          };
          return typedResponse.data.createPatientCase.id;
        });

        appointmentPatientCasePairsToCreatePromises = newPatientCaseIDs.map(
          (patientCaseId: string) =>
            this.client.graphql({
              query: createAppointmentRecordPatientCaseReturningPairID,
              variables: {
                input: {
                  appointmentRecordId: appointment.id,
                  patientCaseId,
                },
              },
            }),
        );
      }
    }

    let createdHistoryOfInjuryNotesID = "";
    let createdCurrentComplaintNotesID = "";

    const awsTimestamp = new Date().getTime();

    if (appointment.historyOfInjuryNotes?.id) {
      await this.client.graphql({
        query: updateNotesReturningID,
        variables: {
          input: {
            id: appointment.historyOfInjuryNotes?.id,
            content: formData.historyOfInjury.content.notes,
          },
        },
      });
    } else {
      const createHistoryOfInjuryNotes = await this.client.graphql({
        query: createNotesReturningID,
        variables: {
          input: {
            content: formData.historyOfInjury.content.notes,
            appointmentrecordID: appointment.id,
            timeStamp: awsTimestamp,
            notesAuthorId: user?.id || null,
            type: NotesType.HISTORY_OF_INJURY,
          },
        },
      });
      createdHistoryOfInjuryNotesID =
        createHistoryOfInjuryNotes.data.createNotes.id;
    }

    if (appointment.currentComplaintNotes?.id) {
      await this.client.graphql({
        query: updateNotesReturningID,
        variables: {
          input: {
            id: appointment.currentComplaintNotes?.id,
            content: formData.currentComplaint.content.notes,
          },
        },
      });
    } else {
      const createCurrentComplaintNotes = await this.client.graphql({
        query: createNotesReturningID,
        variables: {
          input: {
            content: formData.currentComplaint.content.notes,
            appointmentrecordID: appointment.id,
            timeStamp: awsTimestamp,
            notesAuthorId: user?.id || null,
            type: NotesType.CURRENT_COMPLAINT,
          },
        },
      });
      createdCurrentComplaintNotesID =
        createCurrentComplaintNotes.data.createNotes.id;
    }

    const updateAppointmentInput = pruneUnchangedFields(
      {
        id: appointment.id,
        appointmentRecordHistoryOfInjuryNotesId: createdHistoryOfInjuryNotesID
          ? createdHistoryOfInjuryNotesID
          : appointment.appointmentRecordHistoryOfInjuryNotesId,
        appointmentRecordCurrentComplaintNotesId: createdCurrentComplaintNotesID
          ? createdCurrentComplaintNotesID
          : appointment.appointmentRecordCurrentComplaintNotesId,
        historyCallDate:
          formData.historyCall.content.dateTime.date?.toString() || null,
        historyCallTime: formData.historyCall.content.dateTime.time || null,
        employments: [
          {
            employerName: formData.employerAtTimeOfInjury.content.employer,
            jobTitle: formData.employerAtTimeOfInjury.content.jobTitle,
            ...employmentDates(
              formData.employerAtTimeOfInjury.content.dateOfHire,
              formData.employerAtTimeOfInjury.content.lastWork,
            ),
            hoursPerWeek: formData.employerAtTimeOfInjury.content.hoursPerWeek,
            jobDuties: formData.employerAtTimeOfInjury.content.jobDuties,
            notes: formData.employerAtTimeOfInjury.content.notes,
            currentWorkRestrictions:
              formData.employerAtTimeOfInjury.content.currentWorkRestrictions,
            workStatus: formData.employerAtTimeOfInjury.content.workStatus,
            stillWorkingHere:
              formData.employerAtTimeOfInjury.content.stillWorking,
            departureReason:
              formData.employerAtTimeOfInjury.content.departureReason,
            type: EmploymentType.CURRENT,
          },
          ...formData.postInjuryJobs.content.map((employment) => ({
            employerName: employment.employer,
            jobTitle: employment.jobTitle,
            ...employmentDates(employment.dateOfHire, employment.lastWork),
            hoursPerWeek: employment.hoursPerWeek,
            jobDuties: employment.jobDuties,
            notes: employment.notes,
            currentWorkRestrictions: employment.currentWorkRestrictions,
            workStatus: employment.workStatus,
            stillWorkingHere: employment.stillWorking,
            type: EmploymentType.POST_INJURY,
          })),
          ...formData.previousEmployers.content.map((employment) => ({
            employerName: employment.employer,
            jobTitle: employment.jobTitle,
            ...employmentDates(employment.startDate, employment.endDate),
            hoursPerWeek: employment.hoursPerWeek,
            jobDuties: employment.jobDuties,
            notes: employment.notes,
            type: EmploymentType.PREVIOUS,
          })),
        ],
        employmentGaps: formData.employmentGaps.content.map((gap) => {
          reportIncoherentDate("EmploymentGapRecord.startDate", gap.startDate);
          reportIncoherentDate("EmploymentGapRecord.endDate", gap.endDate);
          return {
            type: gap.type,
            startDate: toStoredPair(gap.startDate),
            endDate: toStoredPair(gap.endDate),
            notes: gap.notes,
          };
        }),
      },
      baseline.appointment,
    );
    const updateAppointmentPromise = hasFieldUpdates(updateAppointmentInput)
      ? this.client.graphql({
          query: updateAppointmentRecordReturningID,
          variables: { input: updateAppointmentInput },
        })
      : null;

    await Promise.all([
      ...(updatePatientPromise ? [updatePatientPromise] : []),
      ...(updateAppointmentPromise ? [updateAppointmentPromise] : []),
      ...patientCaseUpdatePromises,
      ...appointmentPatientCasePairsToDeletePromises,
      ...patientCasesToDeletePromises,
      ...appointmentPatientCasePairsToCreatePromises,
    ]);
  }

  async updateBillingInfo(
    appointment: AppointmentRecord,
    formData: BillingFormData,
    amountPaid: number,
    subtotal: number,
    /** The record `formData` was hydrated from — see `hydrationBaseline`. */
    hydrationSource: AppointmentRecord | null,
    billingCounter: number | null = null,
  ) {
    const diagnosisMappings = formData.diagnosisCodes.content || [];
    if (diagnosisMappings.some((entry) => !entry.code)) {
      throw new UserError("ICD-10 code is required for each diagnosis");
    }

    // `billingInfo` is one nested map, so any write replaces it whole — which
    // makes an under-hydrated form catastrophic here rather than merely lossy.
    // The dash-view list query selects `billingInfo` but NOT its
    // `serviceDetails` / `diagnosisMappings` arrays, so a form hydrated from a
    // list-seeded record shows no line items and derives amounts of 0 from
    // them. Writing that would delete the stored line items and zero the
    // totals, and the ICD-10 guard above waves it through because an empty
    // array has nothing to validate.
    //
    // `undefined` vs `null` is the reliable signal: a field absent from the
    // selection set is missing from the response, while one that was selected
    // and empty comes back `null`/`[]`. So if the baseline cannot tell us
    // whether line items exist, an empty payload means "not loaded", not
    // "cleared", and must not be written.
    const emptyLineItems =
      diagnosisMappings.length === 0 &&
      formData.serviceDetails.content.length === 0;
    if (
      emptyLineItems &&
      hydrationSource?.billingInfo?.serviceDetails === undefined
    ) {
      return;
    }

    const billingInput = pruneUnchangedFields(
      {
        id: appointment.id,
        billingInfo: {
          amountPaid: amountPaid,
          totalCharge: subtotal,
          diagnosisMappings: diagnosisMappings.map(
            ({ code, pointer, description }) => ({
              code,
              pointer,
              ...(description ? { description } : {}),
            }),
          ),
          serviceDetails: formData.serviceDetails.content.map(
            convertFormDataServiceDetailToQueryInput,
          ),
          paidDate: formData.billingDates.content.paidDate
            ? formData.billingDates.content.paidDate.toString()
            : null,
          billedDate: formData.billingDates.content.billedDate
            ? formData.billingDates.content.billedDate.toString()
            : null,
          correctionDate: formData.billingDates.content.correctionDate
            ? formData.billingDates.content.correctionDate.toString()
            : null,
          ...(billingCounter !== null && { billingCounter }),
        },
      },
      hydrationSource,
    );

    if (!hasFieldUpdates(billingInput)) {
      return;
    }

    await this.client.graphql({
      query: updateAppointmentRecordReturningID,
      variables: { input: billingInput },
    });
  }

  async createSupplementalFromExistingAppointment(
    appointment: AppointmentRecord,
    dateTime: { date: string },
    schedulingAssistantId: string,
    requestDate?: DateISO,
    autoAssignData?: {
      allAssistants: MedicalAssistant[];
      allDoctors: Doctor[];
      allLocations: Location[];
      allOnsiteOpsConfigs: OnsiteOpsConfig[];
      globalSettings: OpsGlobalSettings | null;
    },
  ) {
    // Clone via the ECS create-from-existing endpoint, which reads the whole
    // clone from the source row server-side. Carried divergences (see
    // appointmentApiAdapter.ts): `schedulingAssistantId` + the auto-assigned
    // on-site op are NOT applied — the endpoint copies both from the source.
    const newAppointmentID = await apiCreateFromExistingAppointment({
      cloneType: "SUPPLEMENTAL",
      sourceAppointmentId: appointment.id,
      date: dateTime.date,
      inquiryDate: requestDate?.toString(),
    });
    // Endpoint writes no junction rows (read-time inheritance) — keep them on
    // the FE until PORTAL_READ_FILES_BY_SOURCE_APPOINTMENT is GA'd.
    await this.inheritClonedAppointmentFiles(
      appointment,
      newAppointmentID,
      AppointmentType.SUPPLEMENTAL,
      false,
    );
    return newAppointmentID;
  }

  /**
   * Resolve the deposition appointment duration: an explicit override wins,
   * else the doctor's `defaultAppointmentDurationMinutes.deposition` (inline on
   * the loaded appointment, else a fetch), else `undefined` (falls through to
   * the DDB/server default).
   */
  private async resolveDepositionDuration(
    appointment: AppointmentRecord,
    appointmentDuration?: number,
  ): Promise<number | undefined> {
    if (appointmentDuration !== undefined) {
      return appointmentDuration;
    }
    const inlineDefault =
      appointment.doctor?.defaultAppointmentDurationMinutes?.deposition;
    if (typeof inlineDefault === "number") {
      return inlineDefault;
    }
    if (appointment.doctorID) {
      try {
        const doctor = await doctorService.getDoctor(appointment.doctorID);
        const doctorDefault =
          doctor?.defaultAppointmentDurationMinutes?.deposition;
        if (typeof doctorDefault === "number") {
          return doctorDefault;
        }
      } catch (error) {
        console.warn("Failed to resolve deposition duration default", error);
      }
    }
    return undefined;
  }

  async createDepositionFromExistingAppointment(
    appointment: AppointmentRecord,
    dateTime: { date: string; time: string },
    schedulingAssistantId: string,
    appointmentDuration?: number,
    autoAssignData?: {
      allAssistants: MedicalAssistant[];
      allDoctors: Doctor[];
      allLocations: Location[];
      allOnsiteOpsConfigs: OnsiteOpsConfig[];
      globalSettings: OpsGlobalSettings | null;
    },
  ) {
    const resolvedAppointmentDuration = await this.resolveDepositionDuration(
      appointment,
      appointmentDuration,
    );

    // Clone via the ECS create-from-existing endpoint (clone read from the
    // source row server-side). ME-2: pass the resolved doctor-default duration
    // so the clone isn't stuck with the source (e.g. QME) duration — the
    // endpoint keeps the source's when this is null. Same source-copy
    // divergences as supplemental (schedulingAssistantId + on-site op).
    const newAppointmentID = await apiCreateFromExistingAppointment({
      cloneType: "DEPOSITION",
      sourceAppointmentId: appointment.id,
      date: dateTime.date,
      time: dateTime.time,
      durationMinutes: resolvedAppointmentDuration,
    });
    // Endpoint writes no junction rows (read-time inheritance) — keep them on
    // the FE until PORTAL_READ_FILES_BY_SOURCE_APPOINTMENT is GA'd.
    await this.inheritClonedAppointmentFiles(
      appointment,
      newAppointmentID,
      AppointmentType.DEPOSITION,
      false,
    );
    return newAppointmentID;
  }

  /**
   * Creates a deposition hold appointment from an existing appointment.
   *
   * The hold is a full clone of the source appointment (patients, cases, notes,
   * files) but with status `DEPOSITION_HOLD` and multi-timeslots. The original
   * appointment's `depositionAppointmentIDs` is updated to reference the hold.
   *
   * After confirming a timeslot via `confirmDepositionHold`, the hold transitions
   * to `SCHEDULED` with the single selected date/time and appointment docs are
   * created.
   */
  async createDepositionHold(
    appointment: AppointmentRecord,
    primaryDateTime: { date: string; time: string },
    additionalTimeslots: Array<{ date: string; time: string }>,
    schedulingAssistantId: string,
    appointmentDuration?: number,
    autoAssignData?: {
      allAssistants: MedicalAssistant[];
      allDoctors: Doctor[];
      allLocations: Location[];
      allOnsiteOpsConfigs: OnsiteOpsConfig[];
      globalSettings: OpsGlobalSettings | null;
    },
    additionalFields?: {
      inquiryDate?: string | null;
      inquiryParty?: InquiringParty | null;
    },
  ) {
    try {
      const resolvedAppointmentDuration = await this.resolveDepositionDuration(
        appointment,
        appointmentDuration,
      );

      const patientCases = sortedPatientCases(appointment);
      if (patientCases.length === 0) {
        throw new Error("No patient cases found for appointment");
      }
      const patient = patientCases[0]?.patientCase.patient;
      const firstClaimNumber = patientCases[0]?.patientCase.claimNumber;

      if (!patient || !firstClaimNumber) {
        throw new Error("Patient or claim number not found");
      }

      const patientResponse = await this.client.graphql({
        query: createPatient,
        variables: {
          input: buildCreatePatientInputFromExistingPatientInput(patient),
        },
      });

      const newPatientID = patientResponse.data.createPatient.id;

      const patientCasePromises = patientCases
        .filter((pair): pair is AppointmentRecordPatientCase => pair !== null)
        .map((appointmentPatientCasePair) =>
          this.client.graphql({
            query: createPatientCaseReturningID,
            variables: {
              input: buildCreatePatientCaseInputFromExistingPatientCaseInput(
                appointmentPatientCasePair,
                newPatientID,
              ),
            },
          }),
        );
      const patientCaseResponses = await Promise.all(patientCasePromises);

      const newPatientCaseIDs = patientCaseResponses.map(
        (response) =>
          (response as { data: { createPatientCase: { id: string } } }).data
            .createPatientCase.id,
      );

      const today = getTodayDateISOStr();
      const resolvedInquiryDate = additionalFields?.inquiryDate || today;
      const newS3FileKey = generateAppointmentS3FileKey(
        firstClaimNumber,
        formatDateStringToISO(primaryDateTime.date),
      );

      // Sanitize additional timeslots
      const sanitizedAdditionalTimeslots = additionalTimeslots
        .filter((ts) => ts.date && ts.time)
        .map((ts) => ({ date: ts.date, time: ts.time }));

      const createInput: CreateAppointmentRecordInput = applyV2BriefingVersion({
        globalPartitionKey: DEFAULT_APPOINTMENT_GLOBAL_PARTITION_KEY,
        date: primaryDateTime.date,
        yearMonth: extractYearMonthFromDateISO(primaryDateTime.date),
        doctorYearMonth: generateDoctorYearMonths(
          appointment.doctorID,
          primaryDateTime.date,
        ),
        time: primaryDateTime.time,
        additionalTimeslots:
          sanitizedAdditionalTimeslots.length > 0
            ? sanitizedAdditionalTimeslots
            : undefined,
        inquiryDate: resolvedInquiryDate,
        inquiryYearMonth: extractYearMonthFromDateISO(resolvedInquiryDate),
        type: AppointmentType.DEPOSITION,
        doctorID: appointment.doctorID,
        doctorOwnerCognitoId: appointment.doctorOwnerCognitoId,
        locationID: appointment.locationID,
        // Invariants of "freshly created", matching the server-side creator
        // and cloner (`_build_appointment_input` / `build_clone_input`):
        // `active` makes the row visible to filtered queries, the two statuses
        // seed the search index and the completed-derivation. Stamped here
        // because this path writes the record client-side rather than through
        // the ECS creator, and nothing downstream backfills them — neither
        // `confirmDepositionHold` nor the ECS `/schedule` transition touches
        // any of the three.
        active: true,
        reportStatus: ReportStatus.PENDING,
        paymentStatus: PaymentStatus.PAYMENT_PENDING,
        status: AppointmentStatus.DEPOSITION_HOLD,
        scheduler: removeTypenames(appointment.scheduler),
        inquirerInfo: {
          ...(appointment.inquirerInfo
            ? removeTypenames(appointment.inquirerInfo)
            : {}),
          inquiryDate: resolvedInquiryDate,
          ...(additionalFields
            ? { inquiringParty: additionalFields.inquiryParty ?? null }
            : {}),
        },
        s3FileKey: newS3FileKey,
        originalAppointmentID: appointment.originalAppointmentID,
        depositionSourceAppointmentIDs: [appointment.id],
        employments: removeTypenames(appointment.employments),
        employmentGaps: removeTypenames(appointment.employmentGaps),
        referredBy: appointment.referredBy,
        // Ops role table: a deposition gets no MA, report ops or pre-visit
        // reviewer; billing ops applies to every type, and takes its Ops
        // Settings default when the source has none.
        appointmentRecordBillingOpId:
          (appointment.appointmentRecordBillingOpId ??
            appointment.billingOp?.id) ||
          (autoAssignData
            ? determineDefaultOpsRoles({
                appointmentType: AppointmentType.DEPOSITION,
                doctorId: appointment.doctorID || null,
                allAssistants: autoAssignData.allAssistants,
                allDoctors: autoAssignData.allDoctors,
                globalSettings: autoAssignData.globalSettings,
              }).billingOpId
            : null),
        appointmentRecordSchedulingAssistantId: schedulingAssistantId,
        appointmentRecordOnSiteOpId: autoAssignData
          ? getOnsiteOpIdForAppointment(
              {
                doctorId: appointment.doctorID || null,
                locationId: appointment.locationID || null,
                allAssistants: autoAssignData.allAssistants,
                allDoctors: autoAssignData.allDoctors,
                allLocations: autoAssignData.allLocations,
                allOnsiteOpsConfigs: autoAssignData.allOnsiteOpsConfigs,
                globalSettings: autoAssignData.globalSettings,
              },
              appointment.onSiteOp?.id || null,
            )
          : appointment.onSiteOp?.id,
        isProd: appointment.isProd || false,
        isDemo: appointment.isDemo || undefined,
        primaryPatientCaseID: findNewPrimaryPatientCaseID(
          patientCases,
          appointment,
          newPatientCaseIDs,
        ),
        ...(resolvedAppointmentDuration !== undefined
          ? { durationMinutes: resolvedAppointmentDuration }
          : {}),
        billingInfo: {
          diagnosisMappings: appointment.billingInfo?.diagnosisMappings?.map(
            (mapping) => removeTypenames(mapping),
          ),
        },
        fieldTemplateRef: appointment.fieldTemplateRef,
      });
      const createAppointmentResponse = await this.client.graphql({
        query: createAppointmentRecordReturningID,
        variables: { input: createInput },
      });
      const newAppointmentID =
        createAppointmentResponse.data.createAppointmentRecord.id;

      const linkAppointmentPatientCasePromises = newPatientCaseIDs.map(
        (patientCaseID) =>
          this.client.graphql({
            query: createAppointmentRecordPatientCaseReturningPairID,
            variables: {
              input: {
                appointmentRecordId: newAppointmentID,
                patientCaseId: patientCaseID,
              },
            },
          }),
      );
      await Promise.all(linkAppointmentPatientCasePromises);

      if (appointment.notes?.items) {
        const { createdNoteIdsByType } = await createNotesByType(
          this.client,
          appointment.notes.items,
          newAppointmentID,
        );

        await this.client.graphql({
          query: updateAppointmentRecordReturningID,
          variables: {
            input: {
              id: newAppointmentID,
              appointmentRecordHistoryOfInjuryNotesId:
                createdNoteIdsByType[NotesType.HISTORY_OF_INJURY]?.[0],
              appointmentRecordCurrentComplaintNotesId:
                createdNoteIdsByType[NotesType.CURRENT_COMPLAINT]?.[0],
            },
          },
        });
      }

      // Create inherited file references for the child appointment
      if (appointment.type) {
        try {
          await uploadedFileService.createInheritedFileReferences(
            appointment.id,
            newAppointmentID,
            AppointmentType.DEPOSITION,
            false,
          );
        } catch (error) {
          console.error(
            "Failed to create inherited file references for deposition hold:",
            error,
          );
        }
      }

      // Link the hold to the source appointment
      await this.client.graphql({
        query: updateAppointmentRecordReturningID,
        variables: {
          input: {
            id: appointment.id,
            depositionAppointmentIDs: [
              ...(appointment.depositionAppointmentIDs || []),
              newAppointmentID,
            ],
          },
        },
      });

      return newAppointmentID;
    } catch (error) {
      console.error("Error creating deposition hold appointment:", error);
      throw error;
    }
  }

  /**
   * Confirms a deposition hold by transitioning it from `DEPOSITION_HOLD` to
   * `SCHEDULED` with the selected date/time. Additional timeslots are cleared.
   *
   * Location is always inherited from the appointment (no per-slot location).
   */
  async confirmDepositionHold(
    holdAppointment: AppointmentRecord,
    selectedTimeslot: { date: string; time: string; durationMinutes?: number },
  ) {
    const selectedDate = normalizeNullableString(selectedTimeslot.date);
    const selectedTime = normalizeNullableString(selectedTimeslot.time);

    if (!selectedDate || !selectedTime) {
      throw new Error("Selected timeslot must include both date and time");
    }

    if (holdAppointment.status !== AppointmentStatus.DEPOSITION_HOLD) {
      throw new Error(
        `Expected DEPOSITION_HOLD status but got ${holdAppointment.status}`,
      );
    }

    await this.client.graphql({
      query: updateAppointmentRecordReturningID,
      variables: {
        input: {
          id: holdAppointment.id,
          date: selectedDate,
          yearMonth: extractYearMonthFromDateISO(selectedDate),
          doctorYearMonth: generateDoctorYearMonths(
            holdAppointment.doctorID,
            selectedDate,
          ),
          time: selectedTime,
          additionalTimeslots: [],
          status: AppointmentStatus.SCHEDULED,
          ...(selectedTimeslot.durationMinutes !== undefined
            ? { durationMinutes: selectedTimeslot.durationMinutes }
            : {}),
        },
      },
    });

    return holdAppointment.id;
  }

  /**
   * Filters dataFieldValuesMappings to exclude ADL field values.
   * Used for re-evaluation appointments to force the MA to collect ADL data again.
   */
  private filterDataFieldValuesExcludingADL(
    adlFieldSectionMappings:
      | (ADLFieldSectionMapping | null)[]
      | null
      | undefined,
    dataFieldValuesMappings:
      | (DataFieldValueMapping | null)[]
      | null
      | undefined,
  ): (DataFieldValueMapping | null)[] | null {
    if (!dataFieldValuesMappings || !adlFieldSectionMappings) {
      return dataFieldValuesMappings || null;
    }

    // Extract ADL field IDs from template
    const adlFieldIDs = new Set<string>();
    adlFieldSectionMappings.forEach((mapping) => {
      if (mapping?.ADLFieldIDs) {
        mapping.ADLFieldIDs.forEach((fieldID) => {
          if (fieldID) {
            adlFieldIDs.add(fieldID);
          }
        });
      }
    });

    // Filter out invalid mappings and ADL field mappings
    return dataFieldValuesMappings.filter((mapping) => {
      // Filter out invalid mappings (null, undefined, or missing dataFieldID)
      if (!mapping?.dataFieldID) {
        return false;
      }
      // Filter out ADL field mappings
      return !adlFieldIDs.has(mapping.dataFieldID);
    });
  }

  async createReEvaluationFromExistingAppointment(
    appointment: AppointmentRecord,
    // date/time are required when scheduling; a hold (options.asHold) is
    // inquiry-like and may omit them.
    dateTime: { date: string | null; time: string },
    schedulingAssistantId: string,
    shouldRemoveInterpreter = true,
    inquirerParty?: InquiringParty,
    inquirerEmail?: string,
    inquirerPhoneNumber?: string,
    inquirerPhoneExtension?: string,
    inquirerDate?: string,
    locationId?: string,
    appointmentDuration?: number,
    autoAssignData?: {
      allAssistants: MedicalAssistant[];
      allDoctors: Doctor[];
      allLocations: Location[];
      allOnsiteOpsConfigs: OnsiteOpsConfig[];
      globalSettings: OpsGlobalSettings | null;
    },
    options?: {
      // Create the clone on REEVAL_HOLD (not yet on the calendar) instead of
      // SCHEDULED. The hold is edited on the inquiry-style page and converts
      // to SCHEDULED from there.
      asHold?: boolean;
    },
  ) {
    // A SCHEDULED re-eval is cloned by the ECS create-from-existing endpoint
    // (clone read from the source row server-side; interpreter dropped for
    // re-confirmation when shouldRemoveInterpreter). Carried divergences (see
    // appointmentApiAdapter.ts): the inquirer OVERRIDES (party / email / phone
    // / extension) have no DTO field and are dropped — only inquiryDate is
    // carried; the source's inquirer info is otherwise copied verbatim. Same
    // source-copy divergences for schedulingAssistantId + on-site op.
    //
    // A HOLD still takes the legacy AppSync clone below: the ECS cloner only
    // allows RE_EVALUATION clones onto SCHEDULED (REEVAL_HOLD is Phase 3).
    if (!options?.asHold) {
      const { date } = dateTime;
      if (!date) {
        throw new Error("Date is required when scheduling a re-evaluation");
      }
      const newAppointmentID = await apiCreateFromExistingAppointment({
        cloneType: "RE_EVALUATION",
        sourceAppointmentId: appointment.id,
        date,
        time: dateTime.time,
        inquiryDate: inquirerDate,
        locationId,
        durationMinutes: appointmentDuration,
        shouldRemoveInterpreter,
      });
      // Endpoint writes no junction rows (read-time inheritance) — keep them
      // on the FE until PORTAL_READ_FILES_BY_SOURCE_APPOINTMENT is GA'd. The
      // re-eval type is resolved the same way the hold path resolves it.
      await this.inheritClonedAppointmentFiles(
        appointment,
        newAppointmentID,
        resolveReevaluationType(appointment.type, appointment.s3FileKey),
        false,
      );
      return newAppointmentID;
    }

    try {
      const patientCases = sortedPatientCases(appointment);
      if (patientCases.length === 0) {
        throw new Error("No patient cases found for appointment");
      }
      const patient = patientCases[0]?.patientCase.patient;
      const firstClaimNumber = patientCases[0]?.patientCase.claimNumber;

      if (!patient || !firstClaimNumber) {
        throw new Error("Patient or claim number not found");
      }

      const patientResponse = await this.client.graphql({
        query: createPatient,
        variables: {
          input: buildCreatePatientInputFromExistingPatientInput(patient),
        },
      });

      const newPatientID = patientResponse.data.createPatient.id;

      const patientCasePromises = patientCases
        .filter((pair): pair is AppointmentRecordPatientCase => pair !== null)
        .map((appointmentPatientCasePair) =>
          this.client.graphql({
            query: createPatientCaseReturningID,
            variables: {
              input: buildCreatePatientCaseInputFromExistingPatientCaseInput(
                appointmentPatientCasePair,
                newPatientID,
              ),
            },
          }),
        );
      const patientCaseResponses = await Promise.all(patientCasePromises);

      const newPatientCaseIDs = patientCaseResponses.map(
        (response) =>
          (response as { data: { createPatientCase: { id: string } } }).data
            .createPatientCase.id,
      );

      const newAppointmentType = resolveReevaluationType(
        appointment.type,
        appointment.s3FileKey,
      );

      const today = getTodayDateISOStr();

      // Prepare interpreter field based on shouldRemoveInterpreter flag
      const interpreterValue = shouldRemoveInterpreter
        ? null
        : removeTypenames(appointment.interpreter);

      // Determine onsite op ID using auto-assignment logic
      let onsiteOpId: string | null | undefined = appointment.onSiteOp?.id;
      if (autoAssignData) {
        onsiteOpId = getOnsiteOpIdForAppointment(
          {
            doctorId: appointment.doctorID || null,
            locationId: locationId || appointment.locationID || null,
            allAssistants: autoAssignData.allAssistants,
            allDoctors: autoAssignData.allDoctors,
            allLocations: autoAssignData.allLocations,
            allOnsiteOpsConfigs: autoAssignData.allOnsiteOpsConfigs,
            globalSettings: autoAssignData.globalSettings,
          },
          appointment.onSiteOp?.id || null,
        );
      }

      // Keep the same MA for re-evaluations since they've likely already started work
      const medicalAssistantId = appointment.medicalAssistant?.id;

      // A role the source left blank takes its Ops Settings default.
      const opsRoleDefaults = autoAssignData
        ? determineDefaultOpsRoles({
            appointmentType: newAppointmentType ?? null,
            doctorId: appointment.doctorID || null,
            allAssistants: autoAssignData.allAssistants,
            allDoctors: autoAssignData.allDoctors,
            globalSettings: autoAssignData.globalSettings,
          })
        : null;

      // For re-evaluation appointments, filter out ADL field values so MA collects them again.
      // Fetch the template from fieldTemplateRef to get the ADL field IDs.
      let filteredDataFieldValuesMappings = appointment.dataFieldValuesMappings;
      if (
        appointment.fieldTemplateRef?.id &&
        appointment.fieldTemplateRef?.version
      ) {
        try {
          const template = await fieldTemplateService.getFieldTemplate(
            appointment.fieldTemplateRef.id,
            appointment.fieldTemplateRef.version,
          );
          if (template?.adlFieldSectionMappings) {
            filteredDataFieldValuesMappings =
              this.filterDataFieldValuesExcludingADL(
                template.adlFieldSectionMappings,
                appointment.dataFieldValuesMappings,
              );
          }
        } catch (error) {
          console.warn("Failed to fetch template for ADL filtering:", error);
          // Fall back to unfiltered values if template fetch fails
        }
      }

      // A dateless hold still needs an s3 key — the date segment is only a
      // path component, so fall back to today.
      const newS3FileKey = generateAppointmentS3FileKey(
        firstClaimNumber,
        formatDateStringToISO(dateTime.date ?? today),
      );

      const createInput: CreateAppointmentRecordInput = applyV2BriefingVersion({
        globalPartitionKey: DEFAULT_APPOINTMENT_GLOBAL_PARTITION_KEY,
        // A hold without a full slot stays dateless, like an inquiry. Both
        // fields or neither: the byGlobalDateTime GSI's composite sort key is
        // date#time, so AppSync rejects a date without a time (and "" is not
        // a valid AWSTime).
        ...(dateTime.date && dateTime.time
          ? {
              date: dateTime.date,
              yearMonth: extractYearMonthFromDateISO(dateTime.date),
              doctorYearMonth: generateDoctorYearMonths(
                appointment.doctorID,
                dateTime.date,
              ),
              time: dateTime.time,
            }
          : {}),
        inquiryDate: inquirerDate || today,
        inquiryYearMonth: extractYearMonthFromDateISO(inquirerDate || today),
        type: newAppointmentType,
        doctorID: appointment.doctorID,
        doctorOwnerCognitoId: appointment.doctorOwnerCognitoId,
        locationID: locationId || appointment.locationID,
        // Invariants of "freshly created" — see `createDepositionHold`.
        active: true,
        reportStatus: ReportStatus.PENDING,
        paymentStatus: PaymentStatus.PAYMENT_PENDING,
        status: options?.asHold
          ? AppointmentStatus.REEVAL_HOLD
          : AppointmentStatus.SCHEDULED,
        scheduler: removeTypenames(appointment.scheduler),
        inquirerInfo: {
          ...(appointment.inquirerInfo
            ? removeTypenames(appointment.inquirerInfo)
            : {}),
          ...(inquirerParty ? { inquiringParty: inquirerParty } : {}),
          ...(inquirerDate ? { inquiryDate: inquirerDate } : {}),
          contact: {
            ...(appointment.inquirerInfo && appointment.inquirerInfo.contact
              ? removeTypenames(appointment.inquirerInfo.contact)
              : {}),
            ...(inquirerEmail ? { email: inquirerEmail } : {}),
            ...(inquirerPhoneNumber ? { phone: inquirerPhoneNumber } : {}),
            ...(inquirerPhoneExtension
              ? { phoneExtension: inquirerPhoneExtension }
              : {}),
          },
        },
        interpreter: interpreterValue,
        s3FileKey: newS3FileKey,
        originalAppointmentID: appointment.originalAppointmentID,
        employments: removeTypenames(appointment.employments),
        employmentGaps: removeTypenames(appointment.employmentGaps),
        referredBy: appointment.referredBy,
        panelSpecialty: appointment.panelSpecialty,
        appointmentRecordMedicalAssistantId: medicalAssistantId,
        appointmentRecordSchedulingAssistantId: schedulingAssistantId,
        appointmentRecordOnSiteOpId: onsiteOpId,
        // A re-eval is a visit, so it keeps every ops role the source has.
        appointmentRecordReportOpId:
          (appointment.appointmentRecordReportOpId ??
            appointment.reportOp?.id) ||
          opsRoleDefaults?.reportOpId,
        appointmentRecordBillingOpId:
          (appointment.appointmentRecordBillingOpId ??
            appointment.billingOp?.id) ||
          opsRoleDefaults?.billingOpId,
        appointmentRecordPreVisitReviewerId:
          (appointment.appointmentRecordPreVisitReviewerId ??
            appointment.preVisitReviewer?.id) ||
          opsRoleDefaults?.preVisitReviewerId,
        isProd: appointment.isProd || false,
        isDemo: appointment.isDemo || undefined,
        primaryPatientCaseID: findNewPrimaryPatientCaseID(
          patientCases,
          appointment,
          newPatientCaseIDs,
        ),
        ...(appointmentDuration !== undefined
          ? { durationMinutes: appointmentDuration }
          : {}),
        fieldTemplateRef: appointment.fieldTemplateRef,
        dataFieldValuesMappings: filteredDataFieldValuesMappings,
      });
      const createAppointmentResponse = await this.client.graphql({
        query: createAppointmentRecordReturningID,
        variables: { input: createInput },
      });
      const newAppointmentID =
        createAppointmentResponse.data.createAppointmentRecord.id;

      const linkAppointmentPatientCasePromises = newPatientCaseIDs.map(
        (patientCaseID) =>
          this.client.graphql({
            query: createAppointmentRecordPatientCaseReturningPairID,
            variables: {
              input: {
                appointmentRecordId: newAppointmentID,
                patientCaseId: patientCaseID,
              },
            },
          }),
      );
      await Promise.all(linkAppointmentPatientCasePromises);

      // Only copy over general notes for re-evaluation appointments
      if (appointment.notes?.items) {
        await createNotesByType(
          this.client,
          appointment.notes.items,
          newAppointmentID,
          [NotesType.GENERAL_MA_NOTES],
        );
      }

      // Create inherited file references for the child appointment
      if (appointment.type && newAppointmentType) {
        try {
          await uploadedFileService.createInheritedFileReferences(
            appointment.id,
            newAppointmentID,
            newAppointmentType,
            false,
          );
        } catch (error) {
          // Log but don't fail the appointment creation
          console.error(
            "Failed to create inherited file references for re-evaluation:",
            error,
          );
        }
      }

      return newAppointmentID;
    } catch (error) {
      // Rethrow so callers surface an error instead of silently closing the
      // modal — both call sites (AppointmentDetails handlers) catch and show
      // a notification.
      console.error("Error creating re-evaluation appointment:", error);
      throw error;
    }
  }

  async markReschedulingInProgress(
    appointment: AppointmentRecord,
    requestedBy: ParticipantType | null,
    reason: ReschedulingReason,
    othersReason: string,
    note: string,
    requestedInternally = false,
  ) {
    // The ECS /start-rescheduling endpoint sets the rescheduling fields
    // server-side (single-row, slot kept). No FE post-step — it's a plain
    // transition. `updated_at` is the optimistic-concurrency precondition.
    await apiStartRescheduling({
      appointmentId: appointment.id,
      reason,
      othersReason,
      note,
      requestedBy,
      requestedInternally,
      updatedAt: appointment.updatedAt,
    });
  }

  /**
   * Write the inherited `AppointmentFileReference` junction rows for an API-path
   * clone (create-from-existing + reschedule). The ECS endpoints write none —
   * they rely on read-time chain inheritance gated on
   * `PORTAL_READ_FILES_BY_SOURCE_APPOINTMENT`, which isn't on in prod — so the FE
   * keeps writing the rows (as the legacy paths do) to preserve inherited-doc
   * visibility for junction-scoped consumers. When read-time inheritance is GA'd
   * everywhere, delete this + its call sites (see the NOTE(portal-cutover) in
   * `services/appointment_operations/{cloner,reschedule_clone}.py`). Best-effort:
   * the clone graph already committed server-side, so a failure is logged only.
   */
  private async inheritClonedAppointmentFiles(
    sourceAppointment: AppointmentRecord,
    cloneAppointmentId: string,
    cloneType: AppointmentType | null | undefined,
    writeable: boolean,
  ): Promise<void> {
    if (!cloneType) return;
    try {
      await uploadedFileService.createInheritedFileReferences(
        sourceAppointment.id,
        cloneAppointmentId,
        cloneType,
        writeable,
      );
    } catch (error) {
      // Best-effort: the clone graph already committed, but a silent failure
      // here means the clone is missing inherited docs — report to Sentry.
      errorReporter.reportApplicationError(
        toError(error, "Failed to create inherited file references"),
        {
          tags: {
            component: "AppointmentService",
            operation: "api_clone_inherit_file_references",
          },
          extra: {
            sourceAppointmentId: sourceAppointment.id,
            cloneAppointmentId,
          },
        },
      );
    }
  }

  /**
   * Resolve the reschedule clone's MA + on-site-op. Reschedules within 14 days
   * of the original keep the source's staffing; otherwise (and only when
   * auto-assign data is available) both are re-resolved (on-site via config,
   * MA via the in-browser picker, except under `USE_ECS_MA_ASSIGNMENT`, where
   * the engine decides server-side — see `applyRescheduleStaffingReassignment`).
   * Pure.
   */
  private resolveRescheduleStaffing(
    sourceAppointment: AppointmentRecord,
    newDate: string,
    newLocationID: string | null,
    autoAssignData?: {
      allAssistants: MedicalAssistant[];
      allDoctors: Doctor[];
      allLocations: Location[];
      allOnsiteOpsConfigs: OnsiteOpsConfig[];
      globalSettings: OpsGlobalSettings | null;
      useEcsMaAssignment: boolean;
    },
  ): {
    medicalAssistantId: string | null | undefined;
    onsiteOpId: string | null | undefined;
  } {
    // Ops role table: a supplemental gets no on-site op and a deposition no MA
    // (the `/reschedule` clone drops both, so the patch below must not write
    // the source's back).
    let onsiteOpId: string | null | undefined =
      sourceAppointment.type === AppointmentType.SUPPLEMENTAL
        ? null
        : sourceAppointment.onSiteOp?.id;
    let medicalAssistantId =
      sourceAppointment.type === AppointmentType.DEPOSITION
        ? null
        : sourceAppointment.medicalAssistant?.id;

    const isRescheduleWithin14Days =
      !!sourceAppointment.date &&
      !!newDate &&
      daysBetweenISODates(sourceAppointment.date, newDate) <= 14;

    // A location-independent type (supplemental) keeps the op the backend
    // resolved from the physician's report-ops routing — re-deriving it from
    // doctor+location would put the parent visit's site staffing back on it.
    const isLocationIndependentOnsiteOp =
      !!sourceAppointment.type &&
      LOCATION_INDEPENDENT_ONSITE_OP_TYPES.has(sourceAppointment.type);
    // Briefings have no on-site op, so no default should be filled in.
    const isBriefing = sourceAppointment.type === AppointmentType.BRIEFING;

    if (autoAssignData && !isRescheduleWithin14Days) {
      // The MA below is re-resolved for every type (flag off); only the
      // on-site op is skipped for the location-independent ones and briefings.
      if (!isLocationIndependentOnsiteOp && !isBriefing) {
        onsiteOpId = getOnsiteOpIdForAppointment(
          {
            doctorId: sourceAppointment.doctorID || null,
            locationId: newLocationID,
            allAssistants: autoAssignData.allAssistants,
            allDoctors: autoAssignData.allDoctors,
            allLocations: autoAssignData.allLocations,
            allOnsiteOpsConfigs: autoAssignData.allOnsiteOpsConfigs,
            globalSettings: autoAssignData.globalSettings,
          },
          sourceAppointment.onSiteOp?.id || null,
        );
      }

      if (!autoAssignData.useEcsMaAssignment) {
        const patientLanguage =
          sortedPatientCases(sourceAppointment)[0]?.patientCase.patient
            ?.language || null;
        const autoAssignResult = performAutoAssignMA(
          {
            patientLanguage,
            appointmentType: sourceAppointment.type || null,
            currentMAId: medicalAssistantId,
            allAssistants: autoAssignData.allAssistants,
          },
          true, // Force assignment for new appointment from existing
        );
        if (autoAssignResult.shouldAssign && autoAssignResult.selectedMAId) {
          medicalAssistantId = autoAssignResult.selectedMAId;
        }
      }
    }

    return { medicalAssistantId, onsiteOpId };
  }

  /**
   * API-path parity for the MA/on-site reassignment the `/reschedule` endpoint
   * doesn't do. Only patches the freshly-created clone when the FE would
   * actually re-resolve staffing (auto-assign data present AND the new slot is
   * >14 days out); otherwise the endpoint's result already matches.
   *
   * Under `USE_ECS_MA_ASSIGNMENT` the MA is left out of the patch: the server
   * owns it. The endpoint ran the MA engine on a direct clone before returning,
   * and runs it on a hold at confirm (`services/ma_assignment/revalidate.py`),
   * honouring the weekly and daily caps the in-browser picker ignores and the
   * history-call owner (GET-4113).
   *
   * Best-effort — the compound write already committed, so a failure is
   * logged, not surfaced.
   */
  private async applyRescheduleStaffingReassignment(
    sourceAppointment: AppointmentRecord,
    cloneAppointmentId: string,
    newDate: string,
    newLocationID: string | null,
    autoAssignData?: {
      allAssistants: MedicalAssistant[];
      allDoctors: Doctor[];
      allLocations: Location[];
      allOnsiteOpsConfigs: OnsiteOpsConfig[];
      globalSettings: OpsGlobalSettings | null;
      useEcsMaAssignment: boolean;
    },
  ): Promise<void> {
    if (!autoAssignData) return;
    const isRescheduleWithin14Days =
      !!sourceAppointment.date &&
      !!newDate &&
      daysBetweenISODates(sourceAppointment.date, newDate) <= 14;
    if (isRescheduleWithin14Days) return;

    const { medicalAssistantId, onsiteOpId } = this.resolveRescheduleStaffing(
      sourceAppointment,
      newDate,
      newLocationID,
      autoAssignData,
    );

    const input: UpdateAppointmentRecordInput = {
      id: cloneAppointmentId,
      appointmentRecordOnSiteOpId: normalizeNullableId(onsiteOpId),
    };
    if (!autoAssignData.useEcsMaAssignment) {
      input.appointmentRecordMedicalAssistantId =
        normalizeNullableId(medicalAssistantId);
    }

    try {
      await this.client.graphql({
        query: updateAppointmentRecordReturningID,
        variables: { input },
      });
    } catch (error) {
      // Best-effort: the clone already committed; a silent failure here leaves
      // the clone on the source's MA/on-site — report to Sentry.
      errorReporter.reportApplicationError(
        toError(error, "Failed to apply reschedule staffing reassignment"),
        {
          tags: {
            component: "AppointmentService",
            operation: "api_reschedule_staffing_reassignment",
          },
          extra: {
            sourceAppointmentId: sourceAppointment.id,
            cloneAppointmentId,
          },
        },
      );
    }
  }

  /**
   * Flag-ON hybrid reschedule (direct + hold). The `/reschedule` endpoint owns
   * the atomic clone + source transition + field carry-over + insights +
   * history-call plan + bidirectional link server-side; the FE keeps the two
   * behaviors the endpoint defers (see appointmentApiAdapter.ts module note):
   * MA/on-site reassignment (patch) and Google-Doc duplication (direct only —
   * a hold duplicates its docs at confirm time). Returns the clone id to match
   * the legacy method's return.
   */
  private async createRescheduledViaApi(params: {
    appointment: AppointmentRecord;
    requestedBy: ParticipantType | null;
    isLate: boolean;
    reason: ReschedulingReason;
    othersReason: string;
    note: string;
    dateTime: { date: string; time: string; locationID?: string | null };
    schedulingAssistantId: string;
    shouldRemoveInterpreter: boolean;
    lambdaService: WrappedServiceModule<CombinedLambdaServices>;
    autoAssignData?: {
      allAssistants: MedicalAssistant[];
      allDoctors: Doctor[];
      allLocations: Location[];
      allOnsiteOpsConfigs: OnsiteOpsConfig[];
      globalSettings: OpsGlobalSettings | null;
      useEcsMaAssignment: boolean;
    };
    requestedInternally: boolean;
    isHold: boolean;
    additionalTimeslots: Array<{
      date: string;
      time: string;
      locationID?: string | null;
    }>;
    requesterDisplayName?: string;
  }): Promise<string> {
    const { appointment, dateTime, autoAssignData, isHold, lambdaService } =
      params;
    const resolvedPrimaryLocationID = normalizeNullableId(
      dateTime.locationID ?? appointment.locationID,
    );

    const newAppointmentID = await apiRescheduleFromExisting({
      sourceAppointmentId: appointment.id,
      reason: params.reason,
      othersReason: normalizeNullableString(params.othersReason),
      date: dateTime.date,
      time: dateTime.time,
      locationId: resolvedPrimaryLocationID,
      isHold,
      isLate: params.isLate,
      requestedBy: params.requestedBy,
      requestedInternally: params.requestedInternally,
      note: params.note,
      requesterDisplayName: params.requesterDisplayName,
      schedulingAssistantId: params.schedulingAssistantId,
      shouldRemoveInterpreter: params.shouldRemoveInterpreter,
      additionalTimeslots: isHold ? params.additionalTimeslots : [],
    });

    // Post-API parity step 1: MA/on-site reassignment past 14 days.
    await this.applyRescheduleStaffingReassignment(
      appointment,
      newAppointmentID,
      dateTime.date,
      resolvedPrimaryLocationID,
      autoAssignData,
    );

    // Post-API parity step 2: inherited AppointmentFileReference junction rows
    // (the endpoint writes none — see inheritClonedAppointmentFiles). Reschedules
    // keep the same type → INHERITED_WRITEABLE (the `true`).
    await this.inheritClonedAppointmentFiles(
      appointment,
      newAppointmentID,
      appointment.type,
      true,
    );

    // Post-API parity step 3: Google-Doc duplication (direct only). Best-effort —
    // the reschedule already committed server-side, so a doc failure must not
    // surface to the user as "reschedule failed".
    if (!isHold) {
      try {
        await this.duplicateAndInheritGoogleDocs(
          appointment,
          newAppointmentID,
          lambdaService,
        );
      } catch (docError) {
        // Best-effort: the reschedule already committed server-side; a silent
        // failure here leaves the clone without duplicated docs — report to Sentry.
        errorReporter.reportApplicationError(
          toError(docError, "Failed to duplicate Google Docs for reschedule"),
          {
            tags: {
              component: "AppointmentService",
              operation: "api_reschedule_duplicate_google_docs",
            },
            extra: {
              sourceAppointmentId: appointment.id,
              cloneAppointmentId: newAppointmentID,
            },
          },
        );
      }
    }

    return newAppointmentID;
  }

  async createRescheduledFromExistingAppointment(
    appointment: AppointmentRecord,
    requestedBy: ParticipantType | null,
    isLate: boolean,
    reason: ReschedulingReason,
    othersReason: string,
    note: string,
    dateTime: { date: string; time: string; locationID?: string | null },
    schedulingAssistantId: string,
    shouldRemoveInterpreter = true,
    lambdaService: WrappedServiceModule<CombinedLambdaServices>,
    autoAssignData?: {
      allAssistants: MedicalAssistant[];
      allDoctors: Doctor[];
      allLocations: Location[];
      allOnsiteOpsConfigs: OnsiteOpsConfig[];
      globalSettings: OpsGlobalSettings | null;
      useEcsMaAssignment: boolean;
    },
    requestedInternally = false,
    holdOptions?: {
      isHold?: boolean;
      additionalTimeslots?: HoldAdditionalTimeslot[];
    },
    requesterDisplayName?: string,
  ) {
    // HYBRID: the ECS /reschedule endpoint owns the compound write; the FE
    // keeps the doc-duplication + MA/on-site reassignment the endpoint defers.
    return this.createRescheduledViaApi({
      appointment,
      requestedBy,
      isLate,
      reason,
      othersReason,
      note,
      dateTime,
      schedulingAssistantId,
      shouldRemoveInterpreter,
      lambdaService,
      autoAssignData,
      requestedInternally,
      isHold: holdOptions?.isHold === true,
      additionalTimeslots:
        sanitizeHoldAdditionalTimeslots(holdOptions?.additionalTimeslots) ?? [],
      requesterDisplayName,
    });
  }

  async createRescheduleHoldFromExistingAppointment(
    appointment: AppointmentRecord,
    requestedBy: ParticipantType | null,
    isLate: boolean,
    reason: ReschedulingReason,
    othersReason: string,
    note: string,
    dateTime: { date: string; time: string; locationID?: string | null },
    additionalTimeslots: Array<{
      date: string;
      time: string;
      locationID?: string | null;
    }>,
    schedulingAssistantId: string,
    lambdaService: WrappedServiceModule<CombinedLambdaServices>,
    autoAssignData?: {
      allAssistants: MedicalAssistant[];
      allDoctors: Doctor[];
      allLocations: Location[];
      allOnsiteOpsConfigs: OnsiteOpsConfig[];
      globalSettings: OpsGlobalSettings | null;
      useEcsMaAssignment: boolean;
    },
    requestedInternally = false,
    requesterDisplayName?: string,
  ) {
    // Hold reschedule intentionally creates a child appointment instead of
    // mutating the source appointment date/time. This keeps source history intact
    // while operations coordinate and confirm a final slot.
    return this.createRescheduledFromExistingAppointment(
      appointment,
      requestedBy,
      isLate,
      reason,
      othersReason,
      note,
      dateTime,
      schedulingAssistantId,
      true,
      lambdaService,
      autoAssignData,
      requestedInternally,
      {
        isHold: true,
        additionalTimeslots,
      },
      requesterDisplayName,
    );
  }

  private async duplicateAndInheritGoogleDocs(
    sourceAppointment: AppointmentRecord,
    newAppointmentID: string,
    lambdaService: WrappedServiceModule<CombinedLambdaServices>,
  ): Promise<void> {
    try {
      const documentsToClone: string[] = [];
      if (sourceAppointment.historyOfInjuryFileID) {
        documentsToClone.push(sourceAppointment.historyOfInjuryFileID);
      }
      if (sourceAppointment.medicalRecordsBriefingFileID) {
        documentsToClone.push(sourceAppointment.medicalRecordsBriefingFileID);
      }
      if (sourceAppointment.evaluationFileID) {
        documentsToClone.push(sourceAppointment.evaluationFileID);
      }
      if (sourceAppointment.assessmentFileID) {
        documentsToClone.push(sourceAppointment.assessmentFileID);
      }
      if (sourceAppointment.depositionPreparationFileID) {
        documentsToClone.push(sourceAppointment.depositionPreparationFileID);
      }

      if (documentsToClone.length === 0) {
        return;
      }

      const duplicateResult = await lambdaService.duplicateGoogleDocs(
        documentsToClone,
        newAppointmentID,
      );

      if (duplicateResult.error) {
        throw new Error(
          `Document inheritance failed for appointment ${newAppointmentID}: ${duplicateResult.error.message}`,
        );
      }

      if (!duplicateResult.data) {
        throw new Error(
          `Document inheritance failed for appointment ${newAppointmentID}: No data returned from document duplication`,
        );
      }

      const fileIdMap = new Map<string, string>();
      duplicateResult.data.body.duplicated_files.forEach(
        (file: DuplicatedFile) => {
          fileIdMap.set(file.source_file_id, file.file_id);
        },
      );

      const updateInput: {
        id: string;
        historyOfInjuryFileID?: string;
        medicalRecordsBriefingFileID?: string;
        evaluationFileID?: string;
        assessmentFileID?: string;
        depositionPreparationFileID?: string;
      } = { id: newAppointmentID };

      if (
        sourceAppointment.historyOfInjuryFileID &&
        fileIdMap.has(sourceAppointment.historyOfInjuryFileID)
      ) {
        updateInput.historyOfInjuryFileID = fileIdMap.get(
          sourceAppointment.historyOfInjuryFileID,
        );
      }
      if (
        sourceAppointment.medicalRecordsBriefingFileID &&
        fileIdMap.has(sourceAppointment.medicalRecordsBriefingFileID)
      ) {
        updateInput.medicalRecordsBriefingFileID = fileIdMap.get(
          sourceAppointment.medicalRecordsBriefingFileID,
        );
      }
      if (
        sourceAppointment.evaluationFileID &&
        fileIdMap.has(sourceAppointment.evaluationFileID)
      ) {
        updateInput.evaluationFileID = fileIdMap.get(
          sourceAppointment.evaluationFileID,
        );
      }
      if (
        sourceAppointment.assessmentFileID &&
        fileIdMap.has(sourceAppointment.assessmentFileID)
      ) {
        updateInput.assessmentFileID = fileIdMap.get(
          sourceAppointment.assessmentFileID,
        );
      }
      if (
        sourceAppointment.depositionPreparationFileID &&
        fileIdMap.has(sourceAppointment.depositionPreparationFileID)
      ) {
        updateInput.depositionPreparationFileID = fileIdMap.get(
          sourceAppointment.depositionPreparationFileID,
        );
      }

      if (Object.keys(updateInput).length > 1) {
        await this.client.graphql({
          query: updateAppointmentRecordReturningID,
          variables: { input: updateInput },
        });
      }
    } catch (docError) {
      console.error(
        `Document inheritance failed for appointment ${newAppointmentID}:`,
        docError,
      );
      if (docError instanceof Error) {
        throw docError;
      }
      throw new Error(
        `Document inheritance failed for appointment ${newAppointmentID}: ${String(docError)}`,
      );
    }
  }

  async confirmRescheduleHold(
    appointment: AppointmentRecord,
    selectedTimeslot: {
      date: string;
      time: string;
      locationID?: string | null;
      durationMinutes?: number;
    },
    lambdaService: WrappedServiceModule<CombinedLambdaServices>,
    userGroup?: AuthUserGroup,
  ) {
    const { sourceAppointment, holdAppointment } =
      await this.resolveRescheduleHoldPair(appointment, userGroup);
    const selectedDate = normalizeNullableString(selectedTimeslot.date);
    const selectedTime = normalizeNullableString(selectedTimeslot.time);
    const selectedLocationID = normalizeNullableId(selectedTimeslot.locationID);

    if (!selectedDate || !selectedTime) {
      throw new Error("Selected timeslot must include both date and time");
    }

    // HYBRID: the ECS /confirm-reschedule endpoint resolves the pair, commits
    // the confirmed slot, transitions the source, and carries the history call
    // server-side; the FE keeps only the Google-Doc duplication the endpoint
    // defers. `updated_at` is the addressed record's (the passed appointment's)
    // optimistic-concurrency precondition.
    await apiConfirmReschedule({
      addressedAppointmentId: appointment.id,
      date: selectedDate,
      time: selectedTime,
      locationId: selectedLocationID,
      durationMinutes: selectedTimeslot.durationMinutes,
      updatedAt: appointment.updatedAt,
    });
    try {
      await this.duplicateAndInheritGoogleDocs(
        sourceAppointment,
        holdAppointment.id,
        lambdaService,
      );
    } catch (docError) {
      // Best-effort: the confirm already committed server-side; a silent
      // failure here leaves the confirmed appt without duplicated docs.
      errorReporter.reportApplicationError(
        toError(
          docError,
          "Failed to duplicate Google Docs for reschedule confirm",
        ),
        {
          tags: {
            component: "AppointmentService",
            operation: "api_reschedule_confirm_duplicate_google_docs",
          },
          extra: {
            sourceAppointmentId: sourceAppointment.id,
            holdAppointmentId: holdAppointment.id,
          },
        },
      );
    }
    return {
      sourceAppointmentId: sourceAppointment.id,
      holdAppointmentId: holdAppointment.id,
    };
  }

  async cancelRescheduleHold(
    appointment: AppointmentRecord,
    userGroup?: AuthUserGroup,
  ) {
    const { sourceAppointment, holdAppointment } =
      await this.resolveRescheduleHoldPair(appointment, userGroup);

    // The ECS /cancel-reschedule endpoint discards the hold and restores the
    // source (from either side) server-side. No FE post-step — cancel
    // duplicates nothing. `updated_at` is the addressed record's (the passed
    // appointment's) optimistic-concurrency precondition.
    await apiCancelReschedule({
      addressedAppointmentId: appointment.id,
      updatedAt: appointment.updatedAt,
    });
    return {
      sourceAppointmentId: sourceAppointment.id,
      holdAppointmentId: holdAppointment.id,
    };
  }

  /**
   * Cancels an in-progress rescheduling and returns the appointment to
   * SCHEDULED (or NO_SHOW if it was originally a no-show), clearing the
   * rescheduling fields.
   *
   * When a reschedule hold was already created (`rescheduledToAppointmentID`
   * set), the hold is cancelled too as a side effect by delegating to
   * `cancelRescheduleHold`. Appointments left in `RESCHEDULING_IN_PROGRESS`
   * without any hold are handled by resetting the source appointment directly,
   * so the action works in both cases.
   */
  async cancelRescheduling(
    appointment: AppointmentRecord,
    userGroup?: AuthUserGroup,
  ): Promise<{
    sourceAppointmentId: string;
    holdAppointmentId: string | null;
  }> {
    // A hold exists — cancel it and reset the source in one consistent flow.
    if (appointment.rescheduledToAppointmentID) {
      return this.cancelRescheduleHold(appointment, userGroup);
    }

    // No hold was created yet — the ECS /cancel-reschedule endpoint resolves
    // the hold-less source server-side and resets it to SCHEDULED/NO_SHOW,
    // clearing the rescheduling fields. It returns an empty
    // originalAppointmentId (no hold). `updated_at` is the
    // optimistic-concurrency precondition.
    await apiCancelReschedule({
      addressedAppointmentId: appointment.id,
      updatedAt: appointment.updatedAt,
    });
    return {
      sourceAppointmentId: appointment.id,
      holdAppointmentId: null,
    };
  }

  /**
   * Fetches a single appointment record by its ID using an optimized GraphQL query.
   *
   * This method retrieves a complete appointment record including all related data
   * such as patient cases, notes, medical conditions, and other associated entities.
   * It uses an optimized query to reduce data transfer and improve performance.
   *
   * When a userGroup is provided, the query is further optimized based on the user's role:
   * - DOCTOR: Uses a lighter query that excludes ops-specific fields (billing, notes, etc.)
   * - Other roles: Uses the full optimized query with all fields
   *
   * @param id - The unique identifier of the appointment record to fetch
   * @param userGroup - Optional user group for role-based query optimization
   * @returns Promise that resolves to the AppointmentRecord if found, or null if not found
   *
   * @throws {Error} When the GraphQL query fails due to network issues, authentication, or server errors
   *
   * @example
   * ```typescript
   * // Fetch an appointment by ID (full query)
   * const appointment = await appointmentService.fetchAppointmentById("appointment-123");
   *
   * // Fetch with role-based optimization
   * const appointment = await appointmentService.fetchAppointmentById("appointment-123", AuthUserGroup.DOCTOR);
   * ```
   */
  async fetchAppointmentById(
    id: string,
    userGroup?: AuthUserGroup,
  ): Promise<AppointmentRecord | null> {
    // Use optimized query for doctors that excludes ops-specific fields
    const query =
      userGroup === AuthUserGroup.DOCTOR
        ? getAppointmentRecordForDoctorOptimized
        : getAppointmentRecordOptimized;

    const response = await this.client.graphql(
      {
        query,
        variables: { id },
      },
      { errorMode: "partial" },
    );

    if (response.data?.getAppointmentRecord) {
      return response.data.getAppointmentRecord as AppointmentRecord;
    }

    // Distinguish "not found" from "unauthorized": when the top-level query
    // returns null data with auth errors, the user lacks access entirely.
    // The graphql client wrapper maps partialErrors into the standard `errors` field.
    const errors = response.errors as GraphQLErrorLike[] | undefined;
    if (errors && hasAnyErrorOfType(errors, GraphQLErrorType.UNAUTHORIZED)) {
      throw new UnauthorizedAccessError("appointment", id);
    }

    return null;
  }

  /**
   * Fetches a prior appointment's persisted OpinionRecap (plus the date and
   * reportGenerationFileID needed to pick the most recent appt with a report).
   * Lightweight alternative to fetchAppointmentById that avoids the heavy
   * optimized query — used to surface prior opinions in the assessment workspace.
   *
   * @param id - The appointment record ID
   * @returns Minimal appointment data with opinionRecap, or null if not found
   */
  async fetchAppointmentOpinionRecap(
    id: string,
  ): Promise<AppointmentRecord | null> {
    const response = await this.client.graphql(
      {
        query: getAppointmentOpinionRecap,
        variables: { id },
      },
      { errorMode: "partial" },
    );

    return (
      (response.data?.getAppointmentRecord as AppointmentRecord | null) ?? null
    );
  }

  /**
   * Fetches minimal appointment data needed for audit logs page.
   * This is a lightweight alternative to fetchAppointmentById that only retrieves
   * the IDs needed to traverse audit logs for related entities.
   *
   * @param id - The appointment record ID
   * @returns Minimal appointment data with only IDs, or null if not found
   *
   * @example
   * ```typescript
   * const appointment = await appointmentService.fetchAppointmentForAuditLogs("appointment-123");
   * if (appointment) {
   *   // Use for audit log traversal
   *   const auditLogs = await auditLogService.fetchAuditLogsForAppointment(appointment, coverLetterFileIds);
   * }
   * ```
   */
  async fetchAppointmentForAuditLogs(
    id: string,
  ): Promise<AppointmentRecord | null> {
    const response = await this.client.graphql({
      query: getAppointmentForAuditLogs,
      variables: { id },
    });

    if (!response.data?.getAppointmentRecord) {
      return null;
    }

    const appointment = response.data
      .getAppointmentRecord as unknown as AppointmentRecord;

    // Extract IntroParagraph from the separate query result
    // The query returns introParagraphsByAppointmentRecordID alongside getAppointmentRecord
    // Type assertion: GraphQL response includes introParagraphsByAppointmentRecordID from network
    interface AuditLogsResponse {
      getAppointmentRecord: unknown;
      introParagraphsByAppointmentRecordID?: {
        items?: Array<{ id: string; __typename: string }>;
        __typename: string;
      };
    }

    const introParagraphs = (response.data as unknown as AuditLogsResponse)
      .introParagraphsByAppointmentRecordID?.items;

    if (introParagraphs && introParagraphs.length > 0) {
      // Attach the first IntroParagraph to the appointment object
      // This is necessary for audit log navigation
      Object.assign(appointment, { introParagraph: introParagraphs[0] });
    }

    return appointment;
  }

  /**
   * Fetches and atomically increments the global billing counter using a custom VTL resolver.
   * @returns The new billing counter value
   */
  async fetchAndIncrementBillingCounterAtomic(): Promise<number> {
    try {
      const response = (await this.client.graphql({
        query: INCREMENT_GLOBAL_BILLING_COUNTER_MUTATION,
        variables: {},
      })) as {
        data?: {
          incrementGlobalBillingCounterAtomic?: { count: number | null } | null;
        } | null;
        errors?: { message: string }[];
      };

      if (response.errors && response.errors.length > 0) {
        console.error(
          "GraphQL errors in incrementGlobalBillingCounterAtomic mutation:",
          response.errors,
        );
        throw new Error(
          `Failed to increment billing counter: ${response.errors
            .map((e) => e.message)
            .join(", ")}`,
        );
      }

      const count = response.data?.incrementGlobalBillingCounterAtomic?.count;

      if (typeof count === "number") {
        return count;
      } else {
        console.error(
          "Error in incrementGlobalBillingCounterAtomic mutation response: count is missing or not a number",
          response,
        );
        throw new Error(
          "Failed to increment billing counter or parse response.",
        );
      }
    } catch (error) {
      console.error(
        "Error calling incrementGlobalBillingCounterAtomic mutation:",
        error,
      );
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(
        `An unexpected error occurred while incrementing the billing counter: ${String(
          error,
        )}`,
      );
    }
  }

  /**
   * Searches for appointments using OpenSearch with flexible filtering and role-based access control
   *
   * This method performs a full-text search across appointment data including patient names,
   * claim numbers, doctor names, and other searchable fields. It automatically applies
   * environment filtering (prod vs non-prod) and role-based access controls.
   *
   * @param params - Search parameters object containing:
   *   - query: The search query string to match against appointment fields
   *   - isProd: Boolean flag to filter by production environment
   *   - userGroup: Optional user role for access control (DOCTOR, ADMIN, etc.)
   *   - doctorId: Optional doctor ID for filtering when userGroup is DOCTOR
   *
   * @returns Promise that resolves to an array of appointment search result objects.
   *          Returns empty array if no results found or if search fails gracefully.
   *          Each result contains summary fields like patient_name, claim_number, doctor_name, etc.
   *
   * @throws {Error} When the GraphQL search query fails due to network issues, authentication, or server errors
   *
   * @example
   * ```typescript
   * // Basic search for all appointments
   * const results = await appointmentService.searchAppointments({
   *   query: "John Doe",
   *   isProd: true
   * });
   *
   * // Doctor-specific search with role filtering
   * const doctorResults = await appointmentService.searchAppointments({
   *   query: "knee injury",
   *   isProd: false,
   *   userGroup: AuthUserGroup.DOCTOR,
   *   doctorId: "doctor-123"
   * });
   *
   * // Process search results
   * results.forEach(appointment => {
   *   console.log(`Patient: ${appointment.patient_name}, Claim: ${appointment.primary_claim_number}`);
   * });
   * ```
   *
   * @remarks
   * - The search automatically filters out null results from the response
   * - When userGroup is DOCTOR and doctorId is provided, results are filtered to that doctor's appointments
   * - The search starts from index 0 and uses OpenSearch backend for performance
   * - Results include summary data optimized for search result display, not full appointment details
   */
  async searchAppointments(params: AppointmentSearchParams) {
    const input: SearchAppointmentsQueryVariables = {
      input: {
        query: params.query,
        from: 0,
        filters: {
          is_prod: params.isProd,
          ...(params.userGroup === AuthUserGroup.DOCTOR && params.doctorId
            ? { doctor_id: params.doctorId }
            : {}),
          ...(params.isDemo !== undefined ? { is_demo: params.isDemo } : {}),
        },
      },
    };

    // For Historian users, use an ID-only query to prevent leaking sensitive
    // search summary data for appointments they may not be authorized to view.
    const query =
      params.userGroup === AuthUserGroup.HISTORIAN
        ? searchAppointmentsIdOnly
        : searchAppointmentsCustom;

    const response = await this.client.graphql({
      query,
      variables: input,
    });

    const searchResults = response.data?.searchAppointmentsCustom?.items;
    if (!searchResults) {
      return [];
    }

    return searchResults.filter(
      (item): item is NonNullable<typeof item> => item !== null,
    );
  }

  /**
   * Processes the submission of an appointment form, either by creating a new appointment
   * or updating an existing one. It also handles creating, updating, and deleting associated notes.
   * @param client GraphQL client
   * @param existingAppointment Optional existing appointment record for updates
   * @param formData The form data containing all appointment information
   * @param user The current user
   * @param noInterpreterNeeded Flag indicating if interpreter is needed
   * @param isDemo Flag indicating if the appointment belongs to demo environment
   * @param targetAppointmentStatus The target status for the appointment
   * @param editedNotes A record of notes that have been edited, mapping note ID to new content.
   * @param deletedNoteIds An array of IDs for notes that should be deleted.
   * @returns The ID of the created or updated appointment record, plus whether
   * the post-commit note sync failed (see `AppointmentSubmissionResult`).
   */
  async processAppointmentSubmission(
    existingAppointment: AppointmentRecord | undefined,
    formData: AppointmentFormData,
    user: User | null,
    noInterpreterNeeded: boolean,
    isDemo = false,
    targetAppointmentStatus: AppointmentStatus,
    editedNotes: Record<string, string>,
    deletedNoteIds: string[],
    addressManagementIds?: AddressManagementIdsInput,
    caseID?: string | null,
    // Intake files staged in this session; forwarded to the create-scheduled /
    // schedule endpoints so the server copies them (API path only — see
    // createAppointmentRecord).
    intakeFormS3Keys?: string[],
    flexibleInjuryDatesEnabled = false,
  ): Promise<AppointmentSubmissionResult> {
    let appointmentID: string | null | undefined;
    let noteSyncFailed = false;

    if (existingAppointment) {
      if (targetAppointmentStatus === AppointmentStatus.SCHEDULED) {
        await this.scheduleAppointmentFromInquiry(
          existingAppointment,
          formData,
          user,
          noInterpreterNeeded,
          addressManagementIds,
          intakeFormS3Keys,
          flexibleInjuryDatesEnabled,
        );
        appointmentID = existingAppointment.id;
      } else {
        await this.saveAppointmentDetails(
          existingAppointment,
          formData,
          user,
          noInterpreterNeeded,
          // NewAppointmentPage builds its form from this same record and does
          // not freeze it behind an edit gate, so the record it holds is the
          // hydration source.
          existingAppointment,
          targetAppointmentStatus,
          undefined, // s3FileKey
          undefined, // selectedDoctor
          addressManagementIds,
          flexibleInjuryDatesEnabled,
        );
        appointmentID = existingAppointment.id;
      }
    } else {
      appointmentID = await this.createAppointmentRecord(
        formData,
        user,
        noInterpreterNeeded,
        isDemo,
        targetAppointmentStatus,
        addressManagementIds,
        caseID,
        intakeFormS3Keys,
        flexibleInjuryDatesEnabled,
      );
    }

    if (appointmentID) {
      // The appointment is committed by this point; the note edits/deletes
      // below are a post-commit sync. A failure here must not propagate as "the
      // submission failed" — the caller would drop `appointmentID` on the
      // floor, and the next click would create a *second* appointment (which is
      // exactly how one bad note id produced duplicate appointments in prod).
      // Mirrors the server's own contract for post-commit note work, which
      // reports the appointment as created and the note step as a partial
      // failure. Report it and hand back the id.
      try {
        if (Object.keys(editedNotes).length > 0) {
          const updatePromises = Object.entries(editedNotes).map(
            ([id, content]) =>
              this.client.graphql({
                query: updateNotesReturningID,
                variables: {
                  input: {
                    id,
                    content,
                  },
                },
              }),
          );
          await Promise.all(updatePromises);
        }

        if (deletedNoteIds.length > 0) {
          const deletePromises = deletedNoteIds.map((id) =>
            this.client.graphql({
              query: deleteNotesReturningID,
              variables: {
                input: {
                  id,
                },
              },
            }),
          );
          await Promise.all(deletePromises);
        }
      } catch (error) {
        // The caller turns this into a toast: the appointment landed, the note
        // change did not, and the user is the only one who can re-enter it.
        noteSyncFailed = true;
        errorReporter.reportApplicationError(
          toError(error, "Note sync failed after appointment was saved"),
          {
            tags: {
              component: "AppointmentService",
              operation: "process_appointment_submission_notes",
            },
            extra: {
              appointmentId: appointmentID,
              editedNoteIds: Object.keys(editedNotes),
              deletedNoteIds,
            },
          },
        );
      }
    }

    return { appointmentID, noteSyncFailed };
  }
}

export const appointmentService = AppointmentService.getInstance(graphqlClient);

/****************************/
/***** Helper functions *****/
/****************************/

function sortedPatientCases(appointment: AppointmentRecord) {
  if (appointment?.patientCases?.items) {
    return sortPatientCases(appointment);
  }
  return [];
}

function findNewPrimaryPatientCaseID(
  patientCases: (AppointmentRecordPatientCase | null)[],
  originalAppointment: AppointmentRecord,
  newPatientCaseIDs: string[],
): string | null {
  const originalPrimaryPatientCaseIndex = patientCases.findIndex(
    (pair) => pair?.patientCase.id === originalAppointment.primaryPatientCaseID,
  );
  return originalPrimaryPatientCaseIndex !== -1
    ? newPatientCaseIDs[originalPrimaryPatientCaseIndex]
    : null;
}
